import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getClassMemberDisplayNumbers } from '../utils/classMemberNumbers';
import { createPortal } from 'react-dom';
import { playUiSound } from '../utils/uiSound';
import AutoStatusToast from '../components/common/AutoStatusToast';
import MemberProfileThumb from '../components/common/MemberProfileThumb';
import { SeatRotateIcon } from '../components/common/SeatRotateIcon';
import NoteGroupSelector from '../components/assignmentPoints/NoteGroupSelector';
import ItemCardSettingsModal from '../components/pointItem/ItemCardSettingsModal';
import ClassPointSettingsModal from '../components/pointItem/ClassPointSettingsModal';
import ClassPointGaugeBar from '../components/pointItem/ClassPointGaugeBar';
import ClassPointClaimReveal from '../components/pointItem/ClassPointClaimReveal';
import ItemGrantSummaryModal, {
  type ItemGrantSummaryRow,
} from '../components/pointItem/ItemGrantSummaryModal';
import LevelSettingsModal from '../components/pointItem/LevelSettingsModal';
import MemberItemGrantModal from '../components/pointItem/MemberItemGrantModal';
import MemberItemInventoryModal from '../components/pointItem/MemberItemInventoryModal';
import ProfileImageSelectModal from '../components/classMembers/ProfileImageSelectModal';
import MemberStoreModal, {
  type ProfileCardPurchaseResult,
  type StoreBuyerOption,
  type StoreProfileCardSet,
} from '../components/pointItem/MemberStoreModal';
import PointTypeSetModal from '../components/pointItem/PointTypeSetModal';
import BulkGrantModal from '../components/pointItem/BulkGrantModal';
import GemStarIcon from '../components/pointItem/GemStarIcon';
import PointItemCategoryTabIcon from '../components/pointItem/PointItemCategoryTabIcon';
import PointTypeIcon from '../components/pointItem/PointTypeIcon';
import MemberRecordHistoryModal, {
  type MemberHistoryRecordEdit,
  type MemberHistoryRow,
} from '../components/pointItem/MemberRecordHistoryModal';
import {
  fetchMemberProfileCardUnlocksFresh,
  memberDisplayCoinTotal,
  memberDisplayPointTotal,
  recomputeCoinTotals,
} from '../services/classMemberOwnedDataService';
import PointItemDateCalendarModal from '../components/pointItem/PointItemDateCalendarModal';
import PointTypeSelectModal, {
  type PointTypeSelectResult,
} from '../components/pointItem/PointTypeSelectModal';
import TimeTagSelectModal from '../components/pointItem/TimeTagSelectModal';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { getClassMemberGradeLayoutsMap, replaceGradeMemberLayout } from '../services/classMembersService';
import {
  ensurePointItemNoteGroup,
  getPointItemNote,
  savePointItemNote,
  type PointItemNoteSaveGroup,
} from '../services/pointItemNoteService';
import {
  requestPointItemNoteFromMainTab,
  setLivePointItemNoteDoc,
  shouldApplyRemotePointItemNote,
  subscribePointItemNoteDoc,
} from '../utils/pointItemNoteTabBridge';
import {
  loadPointItemNoteCache,
  loadPointItemNoteLoadedGroupIds,
  savePointItemNoteCache,
} from '../utils/pointItemNoteStorage';
import {
  loadNoteMemberDisplayMode,
  loadNoteMemberInteractionMode,
  loadNoteMemberLayoutMode,
  loadNoteSelectedGroupId,
  resolveNoteSelectedGroupId,
  saveNoteMemberDisplayMode,
  saveNoteMemberInteractionMode,
  saveNoteMemberLayoutMode,
  saveNoteSelectedGroupId,
} from '../utils/noteSelectedGroupPreference';
import { subscribeSelectedClassGroupChanged } from '../utils/selectedClassGroupPreference';
import type { Grade } from '../types/domain';
import type {
  ClassPointSettings,
  GroupMemberPoints,
  ItemCard,
  ItemCardSet,
  ItemCardUserImage,
  MemberCoinRecord,
  MemberItemInstance,
  MemberNoteRecord,
  MemberNoteType,
  MemberPointRecord,
  MemberProfileCardUnlock,
  PointItemNoteDoc,
  PointLevelSettings,
  PointTypeSet,
  ScheduleTimeTag,
} from '../types/pointItemNote';
import {
  emptyClassPointSettings,
  emptyGroupMemberPoints,
  emptyPointItemNoteDoc,
} from '../types/pointItemNote';
import {
  claimClassPointRewards,
  computeClassPointTotal,
  getClaimableClassPointTiers,
  reconcileClassPointCardLocks,
  type ClassPointGroupScope,
} from '../utils/classPointRewardSync';
import { flattenClassPointRewardCards } from '../utils/memberItemStats';
import type { AdminProfileCardSetWithSlots, ProfileCardSetSource } from '../types/profileCard';
import { profileCardSlotDisplayName } from '../types/profileCard';
import {
  getAdminProfileCardSets,
  getWorkspaceProfileCardSettings,
} from '../services/profileCardCatalogService';
import { getWorkspaceProfileCardSets } from '../services/workspaceProfileCardService';
import {
  loadAdminProfileCardSetsCache,
  loadWorkspaceProfileCardSettingsCache,
  loadWorkspaceProfileCardSetsCache,
} from '../utils/profileCardCatalogCache';
import { drawRandomProfileCardSlot } from '../utils/profileCardDraw';
import { profileCardExchangeCoinsForRarity } from '../utils/profileCardRarity';
import {
  cacheClassMemberGradeLayout,
  emptyGradeLayout,
  loadClassMemberGradeLayoutFromStorage,
  type ClassMemberGradeLayout,
  type ClassMemberGroupMeta,
  type ClassMemberRow,
} from '../utils/classMembersStorage';
import { displayClassMemberName } from '../utils/classMemberNameDisplay';
import { preloadProfileImages } from '../utils/profileImageCache';
import { preloadMemberPhotoImages } from '../utils/memberPhotoImageCache';
import {
  readMemberProfileDisplayMode,
} from '../utils/memberProfileDisplay';
import { dateToWeekdayKey } from '../utils/dashboardDateContext';
import {
  buildHalfHourCustomTimeTagLabelForRecord,
  dayKeyFromDate,
  formatScheduleTimeTagLabel,
  getScheduleTimeTagsForGrade,
  resolveCurrentScheduleTimeTag,
  resolveDisplayTimeTagLabel,
} from '../utils/pointItemScheduleTimeTags';
import { syncLevelUpRewardsForMembers, reconcileLevelRewardsOnSettingsChange } from '../utils/levelUpRewardSync';
import {
  countMemberItems,
  findItemCard,
  listUsableActiveItems,
  resolveRemainingUseCount,
} from '../utils/memberItemStats';
import pointIconUrl from '../assets/point.svg';
import { resolveItemCardImageSrc } from '../utils/itemCardImage';

const SEAT_EMPTY_TOAST = '자리배치된 멤버가 없습니다. 번호 보기를 사용하세요.';

type FlatMember = ClassMemberRow & {
  orderIndex: number;
  groupId: string;
  groupName: string;
};

/** 데이터 조회 / 데이터 기록 */
type InteractionMode = 'view' | 'record';
/** 포인트 / 코인 / 아이템 / 기록 */
type DisplayMode = 'points' | 'coins' | 'items' | 'notes';
type LayoutMode = 'number' | 'seat' | 'list';

const DAY_KO = ['일', '월', '화', '수', '목', '금', '토'] as const;
const NOTE_TYPES: MemberNoteType[] = ['일반', '학습', '행동'];

function flattenMembers(layout: ReturnType<typeof emptyGradeLayout>): FlatMember[] {
  const groups = [...layout.groups].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
  );
  const out: FlatMember[] = [];
  for (const g of groups) {
    const rows = layout.membersByGroupId[g.id] ?? [];
    const nums = getClassMemberDisplayNumbers(rows);
    rows.forEach((m, i) => {
      out.push({
        ...m,
        orderIndex: nums[i] ?? i + 1,
        groupId: g.id,
        groupName: g.name?.trim() || '그룹',
      });
    });
  }
  return out;
}

function buildLayoutFromMembers(
  groups: ClassMemberGroupMeta[],
  allMembers: FlatMember[]
): ClassMemberGradeLayout {
  const membersByGroupId: Record<string, ClassMemberRow[]> = {};
  for (const g of groups) {
    membersByGroupId[g.id] = allMembers
      .filter((m) => m.groupId === g.id)
      .slice()
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((m) => {
        const { orderIndex: _o, groupId: _g, groupName: _n, ...row } = m;
        return row;
      });
  }
  return { groups, membersByGroupId };
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function snapshotDoc(doc: PointItemNoteDoc): string {
  return JSON.stringify({
    pointSet: doc.pointSet,
    itemCards: doc.itemCards,
    itemCardImages: doc.itemCardImages ?? [],
    levelSettings: doc.levelSettings,
    classPointSettings: doc.classPointSettings,
    memberPointsByGroup: doc.memberPointsByGroup,
  });
}

/** 에듀코디: 0~(unit-1)=Lv0, unit~(2unit-1)=Lv1 … */
function levelFromTotal(total: number, unit: number): { level: number; progressPct: number } {
  const u = Math.max(1, unit || 50);
  const pts = Math.max(0, total);
  const level = Math.floor(pts / u);
  const progressPct = ((pts % u) / u) * 100;
  return { level, progressPct };
}

/** 에듀코디 PointsTablePage levelColors */
const LEVEL_COLORS = [
  '#FC5555',
  '#FF9933',
  '#CCCC00',
  '#669900',
  '#119473',
  '#0066CC',
  '#9900CC',
  '#920049',
  '#D60093',
];

function getLevelColor(level: number): string {
  if (level % 10 === 0) return 'var(--color-accent-strong, #10504a)';
  return LEVEL_COLORS[(level % 10) - 1] ?? LEVEL_COLORS[0];
}

function getLevelTextColor(level: number): string {
  if (level % 10 === 0) return '#fff';
  return '#fff';
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `${Math.floor(value / 1_000_000)}M`;
  if (value >= 1_000) return `${Math.floor(value / 1_000)}K`;
  return '';
}

function displayMetricStr(value: number): { text: string; digitCount: number; compact: string } {
  const compact = formatCompact(value);
  if (compact) {
    return {
      text: (Math.abs(value) % 1000).toString().padStart(3, '0'),
      digitCount: 3,
      compact,
    };
  }
  const text = Math.trunc(value).toString().padStart(3, '0');
  return { text, digitCount: Math.max(3, Math.trunc(value).toString().length), compact: '' };
}


/** 탭(포인트·코인·아이템·기록)별로 독립된 상태를 유지 — 탭 전환 후 돌아와도 그대로 기억 */
function useTabScopedState<K extends string, T>(
  tab: K,
  makeInitial: () => T
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [byTab, setByTab] = useState<Partial<Record<K, T>>>({});
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const initRef = useRef(makeInitial);
  const fallbackRef = useRef<T | null>(null);
  if (fallbackRef.current === null) fallbackRef.current = initRef.current();
  const value = (byTab[tab] ?? fallbackRef.current) as T;
  const setValue = useCallback<React.Dispatch<React.SetStateAction<T>>>((action) => {
    const key = tabRef.current;
    setByTab((prev) => {
      const cur = (prev[key] ?? fallbackRef.current) as T;
      const next =
        typeof action === 'function' ? (action as (p: T) => T)(cur) : action;
      if (next === cur && key in prev) return prev;
      return { ...prev, [key]: next };
    });
  }, []);
  return [value, setValue];
}

const PointItemNotePage: React.FC<{
  workspaceId: string;
  currentGradeId?: string;
  grades?: Grade[];
  variant?: 'page' | 'presentation';
  /** 프레젠테이션 등에서 호스트가 숨겨진 동안 플로팅 UI를 끄기 위함. 기본 true */
  active?: boolean;
}> = ({ workspaceId, currentGradeId = '', grades = [], variant = 'page', active = true }) => {
  const isPresentation = variant === 'presentation';
  const surfaceActive = active !== false;
  const {
    toastState: saveToastState,
    showSaving,
    showSuccess,
    showError,
    showInfo,
  } = useAutoStatusToast(3000);
  const gradeReady = Boolean(workspaceId && currentGradeId);

  const presentationBoot = useMemo(() => {
    if (!isPresentation || !workspaceId || !currentGradeId) return null;
    const layout =
      loadClassMemberGradeLayoutFromStorage(workspaceId, currentGradeId) ??
      emptyGradeLayout();
    const nextGroups = [...layout.groups].sort(
      (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
    );
    const flat = flattenMembers(layout);
    const doc =
      loadPointItemNoteCache(workspaceId, currentGradeId) ??
      emptyPointItemNoteDoc(workspaceId, currentGradeId);
    const selectedGroupId = resolveNoteSelectedGroupId(
      nextGroups.map((g) => g.id),
      [loadNoteSelectedGroupId('pointItem', workspaceId, currentGradeId)]
    );
    return { doc, groups: nextGroups, members: flat, selectedGroupId };
  }, [isPresentation, workspaceId, currentGradeId]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  /** 선택 그룹의 포인트/코인/기록이 DB에서 로드된 뒤에만 저장 허용 */
  const [groupDataReady, setGroupDataReady] = useState(
    () =>
      !!(
        presentationBoot?.selectedGroupId &&
        presentationBoot.doc.memberPointsByGroup[presentationBoot.selectedGroupId] != null
      )
  );
  const [doc, setDoc] = useState<PointItemNoteDoc>(
    () => presentationBoot?.doc ?? emptyPointItemNoteDoc(workspaceId, currentGradeId)
  );
  const [groups, setGroups] = useState<ClassMemberGroupMeta[]>(
    () => presentationBoot?.groups ?? []
  );
  const [allMembers, setAllMembers] = useState<FlatMember[]>(
    () => presentationBoot?.members ?? []
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(
    () => presentationBoot?.selectedGroupId ?? null
  );

  useEffect(() => {
    return subscribeSelectedClassGroupChanged((detail) => {
      if (detail.workspaceId !== workspaceId.trim()) return;
      if (detail.gradeId !== currentGradeId.trim()) return;
      const ids = new Set(groups.map((g) => g.id));
      if (detail.groupId && ids.has(detail.groupId)) {
        setSelectedGroupId(detail.groupId);
      }
    });
  }, [workspaceId, currentGradeId, groups]);

  const [interactionMode, setInteractionMode] = useState<InteractionMode>('view');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('points');
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('number');
  const [isSeatRotated, setIsSeatRotated] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useTabScopedState<DisplayMode, Set<string>>(
    displayMode,
    () => new Set()
  );

  useEffect(() => {
    if (!workspaceId || !currentGradeId) return;
    const savedLayout = loadNoteMemberLayoutMode('pointItem', workspaceId, currentGradeId);
    if (savedLayout) setLayoutMode(savedLayout);
    const savedDisplay = loadNoteMemberDisplayMode('pointItem', workspaceId, currentGradeId);
    if (savedDisplay) setDisplayMode(savedDisplay);
    const savedInteraction = loadNoteMemberInteractionMode('pointItem', workspaceId, currentGradeId);
    if (savedInteraction) setInteractionMode(savedInteraction);
  }, [workspaceId, currentGradeId]);

  const setDisplayModeAndPersist = useCallback(
    (mode: DisplayMode) => {
      setDisplayMode(mode);
      if (workspaceId && currentGradeId) {
        saveNoteMemberDisplayMode('pointItem', workspaceId, currentGradeId, mode);
      }
    },
    [workspaceId, currentGradeId]
  );

  const setInteractionModeAndPersist = useCallback(
    (mode: InteractionMode | ((prev: InteractionMode) => InteractionMode)) => {
      setInteractionMode((prev) => {
        const next = typeof mode === 'function' ? mode(prev) : mode;
        if (workspaceId && currentGradeId) {
          saveNoteMemberInteractionMode('pointItem', workspaceId, currentGradeId, next);
        }
        return next;
      });
    },
    [workspaceId, currentGradeId]
  );
  const [draftValues, setDraftValues] = useTabScopedState<DisplayMode, Record<string, number>>(
    displayMode,
    () => ({})
  );
  const [hoverRecordMemberId, setHoverRecordMemberId] = useState<string | null>(null);
  /** 지급 플로팅 애니메이션(+N/-N) — 저장 시 학생 블럭 위로 떠오름 + 블럭은 잠시 강하게 흐려짐 */
  const [gainFloats, setGainFloats] = useState<
    Array<{ key: string; kind: 'points' | 'coins'; value: number; x: number; y: number }>
  >([]);
  const gainDimTimersRef = useRef<Map<HTMLElement, number>>(new Map());
  const showGainFloats = useCallback(
    (kind: 'points' | 'coins', deltas: Record<string, number>) => {
      if (typeof window === 'undefined') return;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      const stamp = Date.now();
      const items: Array<{ key: string; kind: 'points' | 'coins'; value: number; x: number; y: number }> = [];
      Object.entries(deltas).forEach(([memberId, value]) => {
        if (!value) return;
        const el = document.querySelector<HTMLElement>(
          `[data-pin-member-id="${CSS.escape(memberId)}"]`
        );
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.width === 0 || r.bottom < 0 || r.top > window.innerHeight) return;
        items.push({ key: `${stamp}-${memberId}`, kind, value, x: r.left + r.width / 2, y: r.top + r.height * 0.5 });
        /* React가 관리하지 않는 data 속성 → 리렌더에 지워지지 않음. 재지급 시 제거→reflow→재설정으로 재시작 */
        el.removeAttribute('data-gain-dim');
        void el.offsetWidth;
        el.setAttribute('data-gain-dim', '');
        const prevT = gainDimTimersRef.current.get(el);
        if (prevT != null) window.clearTimeout(prevT);
        gainDimTimersRef.current.set(
          el,
          window.setTimeout(() => {
            el.removeAttribute('data-gain-dim');
            gainDimTimersRef.current.delete(el);
          }, 1050)
        );
      });
      if (items.length === 0) return;
      playUiSound('chime');
      setGainFloats((prev) => [...prev, ...items]);
      const keys = new Set(items.map((i) => i.key));
      window.setTimeout(() => setGainFloats((prev) => prev.filter((f) => !keys.has(f.key))), 850);
    },
    []
  );
  /** hover 해제 디바운스 — 경계에서 조회↔기록 전환이 반복(깜빡임)되지 않도록 약간 늦게 해제 */
  const hoverLeaveTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (hoverLeaveTimerRef.current != null) window.clearTimeout(hoverLeaveTimerRef.current);
    },
    []
  );
  const [focusRecordMemberId, setFocusRecordMemberId] = useState<string | null>(null);
  const [draftMemos, setDraftMemos] = useTabScopedState<DisplayMode, Record<string, string>>(
    displayMode,
    () => ({})
  );
  const [draftNoteTypes, setDraftNoteTypes] = useTabScopedState<
    DisplayMode,
    Record<string, MemberNoteType>
  >(displayMode, () => ({}));
  const [draftPointTypeIds, setDraftPointTypeIds] = useTabScopedState<
    DisplayMode,
    Record<string, string>
  >(displayMode, () => ({}));
  const [selectedPointTypeId, setSelectedPointTypeId] = useState<string | null>(null);

  const [pointSetOpen, setPointSetOpen] = useState(false);
  const [itemCardOpen, setItemCardOpen] = useState(false);
  const [levelSettingsOpen, setLevelSettingsOpen] = useState(false);
  const [classPointOpen, setClassPointOpen] = useState(false);
  const [classPointClaimReveal, setClassPointClaimReveal] = useState<{
    cards: ItemCard[];
    mode: 'grant' | 'unlock';
  } | null>(null);
  const [pointTypeSelectOpen, setPointTypeSelectOpen] = useState(false);
  const [selectingMemberId, setSelectingMemberId] = useState<string | null>(null);
  const [pointTypeSelectSource, setPointTypeSelectSource] = useState<'member' | 'bulk'>('member');
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkPointTypeId, setBulkPointTypeId] = useState('');
  const [bulkPointValue, setBulkPointValue] = useState('');
  const [bulkPointMemo, setBulkPointMemo] = useState('');
  const [bulkCoinAmount, setBulkCoinAmount] = useState(10);
  const [bulkCoinMemo, setBulkCoinMemo] = useState('');
  const [bulkNoteText, setBulkNoteText] = useState('');
  const [bulkNoteType, setBulkNoteType] = useState<MemberNoteType>('일반');
  const [itemGrantOpen, setItemGrantOpen] = useState(false);
  const [itemGrantTargetIds, setItemGrantTargetIds] = useState<string[]>([]);
  const [itemInventoryMemberId, setItemInventoryMemberId] = useState<string | null>(null);
  const [storeMemberId, setStoreMemberId] = useState<string | null>(null);
  /** 타이틀 스토어: 구매자 미지정(클래스 일괄) */
  const [storeClassOpen, setStoreClassOpen] = useState(false);
  /** 스토어를 프로필 카드 전용으로 열지 (프로필 이미지 선택 모달에서) */
  const [storeProfileOnly, setStoreProfileOnly] = useState(false);
  /** 조회모드 프로필 더블클릭 → 이미지 선택 모달 */
  const [profileSelectMemberId, setProfileSelectMemberId] = useState<string | null>(null);
  /** 헤더 '프로필 이미지 설정' — 전체 학생 일괄 지정 모달 */
  const [bulkProfileOpen, setBulkProfileOpen] = useState(false);
  const [profileUnlocksRefreshKey, setProfileUnlocksRefreshKey] = useState(0);
  /** 프로필 이미지(일러스트) 변경 — 통합 저장 시 DB 반영 */
  const [profileLayoutDirty, setProfileLayoutDirty] = useState(false);
  /** 아이템 기록모드: 스토어 구매 예정(저장 시 확정) */
  const [draftStorePurchases, setDraftStorePurchases] = useTabScopedState<
    DisplayMode,
    Record<
      string,
      Array<{
        draftId: string;
        itemCardId: string;
        cost: number;
        name: string;
        cardImage?: string;
        type: ItemCard['type'];
        useCount?: number;
      }>
    >
  >(displayMode, () => ({}));
  const [profileCardSets, setProfileCardSets] = useState<
    Array<AdminProfileCardSetWithSlots & { source: ProfileCardSetSource; enabled: boolean; priceCoins: number }>
  >([]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;

    const mergeSets = (
      adminSets: Awaited<ReturnType<typeof getAdminProfileCardSets>>,
      adminSettings: Awaited<ReturnType<typeof getWorkspaceProfileCardSettings>>,
      userSets: Awaited<ReturnType<typeof getWorkspaceProfileCardSets>>
    ) => {
      const adminSettingById = new Map(adminSettings.map((s) => [s.setId, s] as const));
      return [
        ...adminSets.map((s) => {
          const setting = adminSettingById.get(s.id);
          return {
            ...s,
            source: 'admin' as const,
            enabled: setting?.enabled ?? false,
            priceCoins: setting?.priceCoins ?? 100,
          };
        }),
        ...userSets.map((s) => ({
          id: s.id,
          name: s.name,
          sortOrder: s.sortOrder,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          slots: s.slots,
          source: 'workspace' as const,
          enabled: s.enabled,
          priceCoins: s.priceCoins ?? 100,
        })),
      ];
    };

    const cachedAdmin = loadAdminProfileCardSetsCache();
    const cachedSettings = loadWorkspaceProfileCardSettingsCache(workspaceId);
    const cachedUser = loadWorkspaceProfileCardSetsCache(workspaceId);
    if (cachedAdmin && cachedSettings && cachedUser) {
      setProfileCardSets(mergeSets(cachedAdmin.sets, cachedSettings.settings, cachedUser.sets));
    }

    Promise.all([
      getAdminProfileCardSets(),
      getWorkspaceProfileCardSettings(workspaceId),
      getWorkspaceProfileCardSets(workspaceId),
    ])
      .then(([adminSets, adminSettings, userSets]) => {
        if (cancelled) return;
        setProfileCardSets(mergeSets(adminSets, adminSettings, userSets));
      })
      .catch(() => {
        /* 프로필 카드 기능은 선택 사항: 실패해도 나머지 화면은 정상 동작해야 함 */
      });
    return () => {
      cancelled = true;
    };
    // itemCardOpen: 설정 모달(프로필 카드 탭)에서 저장 후 닫히면 head 비교로 최신만 다시 불러온다
  }, [workspaceId, itemCardOpen]);

  const [itemSummaryOpen, setItemSummaryOpen] = useState(false);
  const [itemSummaryName, setItemSummaryName] = useState('');
  const [itemSummaryAcquired, setItemSummaryAcquired] = useState<ItemGrantSummaryRow[]>([]);
  const [itemSummaryMissed, setItemSummaryMissed] = useState<ItemGrantSummaryRow[]>([]);
  const [timeTagPickerOpen, setTimeTagPickerOpen] = useState(false);
  const [dateCalendarOpen, setDateCalendarOpen] = useState(false);
  const [historyMemberId, setHistoryMemberId] = useState<string | null>(null);
  const [selectedTimeTag, setSelectedTimeTag] = useState<ScheduleTimeTag | null>(null);
  const [selectedDateIso, setSelectedDateIso] = useState(() => todayIso());
  const [dateEditingField, setDateEditingField] = useState<'year' | 'month' | 'day' | null>(null);
  const [dateTempValue, setDateTempValue] = useState('');
  const timeTagManualRef = useRef(false);

  const selectedDate = useMemo(() => parseIsoDate(selectedDateIso), [selectedDateIso]);

  const baselineRef = useRef('');
  const docRef = useRef(doc);
  docRef.current = doc;
  const modePillAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [modePillFloating, setModePillFloating] = useState(false);

  const isRecordMode = interactionMode === 'record';

  useEffect(() => {
    if (!surfaceActive) {
      setModePillFloating(false);
      return;
    }

    const el = modePillAnchorRef.current;
    const observeTarget =
      el?.closest<HTMLElement>('.cp-pin-pres-top-wrap') ?? el;
    if (!observeTarget || typeof IntersectionObserver === 'undefined') return;

    const mq = window.matchMedia('(max-width: 900px)');
    let observer: IntersectionObserver | null = null;

    const disconnect = () => {
      observer?.disconnect();
      observer = null;
    };

    const sync = () => {
      disconnect();
      if (!mq.matches) {
        setModePillFloating(false);
        return;
      }
      // 상단 네비(~36px)+플로팅 바 여유만큼 줄여, 버튼이 네비/그라데이션에 가리기 전에 플로팅 전환
      observer = new IntersectionObserver(
        ([entry]) => {
          setModePillFloating(!entry.isIntersecting);
        },
        { root: null, threshold: 0, rootMargin: '-72px 0px 0px 0px' }
      );
      observer.observe(observeTarget);
    };

    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
      disconnect();
      setModePillFloating(false);
    };
  }, [loading, isPresentation, groups.length, surfaceActive]);

  const toggleInteractionMode = useCallback(() => {
    setInteractionModeAndPersist((m) => (m === 'view' ? 'record' : 'view'));
    setSelectedMemberIds(new Set());
  }, [setInteractionModeAndPersist]);

  const scheduleTags = useMemo(
    () =>
      gradeReady
        ? getScheduleTimeTagsForGrade({
            workspaceId,
            gradeId: currentGradeId,
            grades,
            date: selectedDate,
          })
        : [],
    [workspaceId, currentGradeId, grades, gradeReady, selectedDate]
  );

  useEffect(() => {
    timeTagManualRef.current = false;
    const now = new Date();
    const sameDay =
      selectedDate.getFullYear() === now.getFullYear() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getDate() === now.getDate();
    setSelectedTimeTag(resolveCurrentScheduleTimeTag(scheduleTags, sameDay ? now : selectedDate));
  }, [scheduleTags, currentGradeId, selectedDate]);

  useEffect(() => {
    if (!scheduleTags.length) return;
    const id = window.setInterval(() => {
      if (timeTagManualRef.current) return;
      const now = new Date();
      const sameDay =
        selectedDate.getFullYear() === now.getFullYear() &&
        selectedDate.getMonth() === now.getMonth() &&
        selectedDate.getDate() === now.getDate();
      if (!sameDay) return;
      setSelectedTimeTag(resolveCurrentScheduleTimeTag(scheduleTags, now));
    }, 30_000);
    return () => window.clearInterval(id);
  }, [scheduleTags, selectedDate]);

  const load = useCallback(async () => {
    if (!gradeReady) {
      setDoc(emptyPointItemNoteDoc(workspaceId, currentGradeId));
      setGroups([]);
      setAllMembers([]);
      setSelectedGroupId(null);
      setProfileLayoutDirty(false);
      baselineRef.current = '';
      setGroupDataReady(false);
      setLoading(false);
      return;
    }
    setGroupDataReady(false);

    const applyLayout = (
      layout: ReturnType<typeof emptyGradeLayout>,
      preferGroupId: string | null
    ) => {
      const nextGroups = [...layout.groups].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
      );
      const flat = flattenMembers(layout);
      setGroups(nextGroups);
      setAllMembers(flat);
      setProfileLayoutDirty(false);
      preloadProfileImages(flat.map((m) => m.profileImage));
      if (readMemberProfileDisplayMode(workspaceId, currentGradeId) === 'photo') {
        preloadMemberPhotoImages(
          flat.map((m) => ({
            path: m.photoProfilePath,
            version: m.photoProfileVersion,
          }))
        );
      }
      const remembered = loadNoteSelectedGroupId('pointItem', workspaceId, currentGradeId);
      const groupId = resolveNoteSelectedGroupId(
        nextGroups.map((g) => g.id),
        [preferGroupId, remembered]
      );
      setSelectedGroupId(groupId);
      if (groupId) {
        saveNoteSelectedGroupId('pointItem', workspaceId, currentGradeId, groupId);
      }
      return groupId;
    };

    /* 메인 프레젠: 절대 불러오는중으로 막지 않음 — LS 즉시 표시 + 메인 탭 워밍/subscribe */
    if (isPresentation) {
      const syncLayout =
        loadClassMemberGradeLayoutFromStorage(workspaceId, currentGradeId) ??
        emptyGradeLayout();
      const syncDoc =
        loadPointItemNoteCache(workspaceId, currentGradeId) ??
        emptyPointItemNoteDoc(workspaceId, currentGradeId);
      const groupId = applyLayout(syncLayout, null);
      setDoc(syncDoc);
      baselineRef.current = snapshotDoc(syncDoc);
      const groupReady =
        !!groupId && syncDoc.memberPointsByGroup[groupId] != null;
      setGroupDataReady(groupReady);
      setLoading(false);
      setLivePointItemNoteDoc(
        syncDoc,
        loadPointItemNoteLoadedGroupIds(workspaceId, currentGradeId)
      );
      requestPointItemNoteFromMainTab({
        workspaceId,
        gradeId: currentGradeId,
        groupId: groupId ?? undefined,
      });
      if (groupId && !groupReady) {
        const memberIds = flattenMembers(syncLayout)
          .filter((m) => m.groupId === groupId)
          .map((m) => m.id);
        void ensurePointItemNoteGroup(
          workspaceId,
          currentGradeId,
          groupId,
          syncDoc,
          memberIds
        ).then((nextDoc) => {
          setDoc((prev) => {
            const nextG = nextDoc.memberPointsByGroup[groupId];
            if (!nextG) return prev;
            const merged = {
              ...prev,
              memberPointsByGroup: {
                ...prev.memberPointsByGroup,
                [groupId]: {
                  ...nextG,
                  coinTotals: recomputeCoinTotals(nextG.coinRecords ?? []),
                },
              },
              updatedAt: nextDoc.updatedAt || prev.updatedAt,
            };
            baselineRef.current = snapshotDoc(merged);
            setLivePointItemNoteDoc(
              merged,
              loadPointItemNoteLoadedGroupIds(workspaceId, currentGradeId)
            );
            return merged;
          });
          setGroupDataReady(true);
        });
      }
      return;
    }

    setLoading(true);
    try {
      const cachedLayout =
        loadClassMemberGradeLayoutFromStorage(workspaceId, currentGradeId) ??
        emptyGradeLayout();
      const cachedGroupId = applyLayout(cachedLayout, null);

      const [settingsDoc, layouts] = await Promise.all([
        getPointItemNote(workspaceId, currentGradeId),
        getClassMemberGradeLayoutsMap(workspaceId, [currentGradeId]),
      ]);
      const layout = layouts[currentGradeId] ?? cachedLayout;
      const groupId = applyLayout(layout, cachedGroupId);
      const layoutMembers = flattenMembers(layout);
      const groupMemberIds = groupId
        ? layoutMembers.filter((m) => m.groupId === groupId).map((m) => m.id)
        : [];
      const nextDoc = groupId
        ? await ensurePointItemNoteGroup(
            workspaceId,
            currentGradeId,
            groupId,
            settingsDoc,
            groupMemberIds
          )
        : settingsDoc;
      setDoc(nextDoc);
      baselineRef.current = snapshotDoc(nextDoc);
      setGroupDataReady(!!groupId);
      setLivePointItemNoteDoc(
        nextDoc,
        loadPointItemNoteLoadedGroupIds(workspaceId, currentGradeId)
      );
    } catch {
      const empty = emptyPointItemNoteDoc(workspaceId, currentGradeId);
      setDoc(empty);
      baselineRef.current = snapshotDoc(empty);
      setGroupDataReady(false);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, currentGradeId, gradeReady, isPresentation]);

  useEffect(() => {
    void load();
  }, [load]);

  const members = useMemo(() => {
    if (!selectedGroupId) return [] as FlatMember[];
    return allMembers.filter((m) => m.groupId === selectedGroupId);
  }, [allMembers, selectedGroupId]);

  const groupPoints = useMemo(() => {
    if (!selectedGroupId) return emptyGroupMemberPoints();
    return doc.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
  }, [doc.memberPointsByGroup, selectedGroupId]);

  const classPointGroupScopes = useMemo((): ClassPointGroupScope[] => {
    const map = new Map<string, string[]>();
    allMembers.forEach((m) => {
      if (!m.groupId) return;
      const arr = map.get(m.groupId);
      if (arr) arr.push(m.id);
      else map.set(m.groupId, [m.id]);
    });
    return Array.from(map.entries()).map(([groupId, memberIds]) => ({
      groupId,
      memberIds,
    }));
  }, [allMembers]);

  const classPointExtraCards = useMemo(
    () => flattenClassPointRewardCards(doc.classPointSettings),
    [doc.classPointSettings]
  );

  const classIntegratedPointTotal = useMemo(() => {
    const settings = doc.classPointSettings ?? emptyClassPointSettings();
    return computeClassPointTotal(doc.memberPointsByGroup, settings, {
      currentGroupId: selectedGroupId,
      currentMemberIds: members.map((m) => m.id),
      allGroups: classPointGroupScopes,
    });
  }, [
    doc.memberPointsByGroup,
    doc.classPointSettings,
    selectedGroupId,
    members,
    classPointGroupScopes,
  ]);

  const classPointClaimableTiers = useMemo(() => {
    const settings = doc.classPointSettings ?? emptyClassPointSettings();
    if (!settings.enabled) return [];
    return getClaimableClassPointTiers(
      doc.memberPointsByGroup,
      classPointGroupScopes,
      settings,
      {
        currentGroupId: selectedGroupId,
        classTotal: classIntegratedPointTotal,
      }
    );
  }, [
    doc.classPointSettings,
    doc.memberPointsByGroup,
    classPointGroupScopes,
    selectedGroupId,
    classIntegratedPointTotal,
  ]);

  /** 포인트 변동 직후 학급 포인트 구간 미만 카드 잠금 (레벨업 sync와 동일 시점) */
  const applyClassPointLocksToDoc = useCallback(
    (d: PointItemNoteDoc): PointItemNoteDoc => {
      const settings = d.classPointSettings ?? emptyClassPointSettings();
      if (!settings.enabled || !selectedGroupId) return d;
      const classTotal = computeClassPointTotal(d.memberPointsByGroup, settings, {
        currentGroupId: selectedGroupId,
        currentMemberIds: members.map((m) => m.id),
        allGroups: classPointGroupScopes,
      });
      const synced = reconcileClassPointCardLocks(
        d.memberPointsByGroup,
        classPointGroupScopes,
        settings,
        { currentGroupId: selectedGroupId, classTotal }
      );
      if (synced.touchedGroupIds.length === 0) return d;
      return {
        ...d,
        memberPointsByGroup: synced.memberPointsByGroup,
      };
    },
    [selectedGroupId, members, classPointGroupScopes]
  );

  const buildSaveGroups = useCallback(
    (groupIds: Array<string | null | undefined>): PointItemNoteSaveGroup[] => {
      const uniq = Array.from(new Set(groupIds.filter((g): g is string => !!g)));
      return uniq.map((groupId) => {
        const fromLayout = allMembers
          .filter((m) => m.groupId === groupId)
          .map((m) => m.id);
        if (fromLayout.length > 0) {
          return { groupId, memberIds: fromLayout };
        }
        // 레이아웃 멤버가 비어도 문서에 있는 멤버 ID는 저장 대상에 포함
        const g = docRef.current.memberPointsByGroup[groupId];
        const ids = new Set<string>();
        (g?.records ?? []).forEach((r) => ids.add(r.memberId));
        (g?.coinRecords ?? []).forEach((r) => ids.add(r.memberId));
        (g?.notes ?? []).forEach((r) => ids.add(r.memberId));
        (g?.itemInstances ?? []).forEach((r) => ids.add(r.memberId));
        return { groupId, memberIds: Array.from(ids) };
      });
    },
    [allMembers]
  );

  /** 그룹 전환 시 해당 그룹만 DB에서 로드 — 다른 그룹 메모리는 유지 */
  useEffect(() => {
    if (!gradeReady || !selectedGroupId) {
      setGroupDataReady(false);
      return;
    }
    // 초기 load()와 레이스하지 않음 — 빈 셸 ensure가 설정을 덮어쓰는 것 방지
    if (loading) return;
    let cancelled = false;
    const groupId = selectedGroupId;
    const groupMemberIds = allMembers
      .filter((m) => m.groupId === groupId)
      .map((m) => m.id);
    // 레이아웃 멤버가 아직 없으면 잘못된 빈 조회로 합산을 지우지 않음
    if (groupMemberIds.length === 0) {
      setGroupDataReady(false);
      return;
    }
    /* 캐시에 그룹 이력이 있으면 즉시 ready (프레젠 브릿지 대기 중 로딩 고정 방지) */
    const cachedGroup = docRef.current.memberPointsByGroup[groupId];
    const groupAlreadyCached =
      cachedGroup != null &&
      (isPresentation ||
        loadPointItemNoteLoadedGroupIds(workspaceId, currentGradeId).includes(groupId));
    if (groupAlreadyCached) {
      setGroupDataReady(true);
      return;
    }
    setGroupDataReady(false);
    void ensurePointItemNoteGroup(
      workspaceId,
      currentGradeId,
      groupId,
      docRef.current,
      groupMemberIds
    ).then((nextDoc) => {
      if (cancelled) return;
      setDoc((prev) => {
        const nextG = nextDoc.memberPointsByGroup[groupId];
        if (!nextG) return prev;
        const hydrated = {
          ...nextG,
          coinTotals: recomputeCoinTotals(nextG.coinRecords ?? []),
        };
        const merged = {
          ...prev,
          memberPointsByGroup: {
            ...prev.memberPointsByGroup,
            [groupId]: hydrated,
          },
          // 그룹 이력만 갱신 — 설정은 prev 유지(빈 ensure 결과로 덮지 않음)
          updatedAt: nextDoc.updatedAt || prev.updatedAt,
        };
        if (baselineRef.current === snapshotDoc(prev)) {
          baselineRef.current = snapshotDoc(merged);
        }
        setLivePointItemNoteDoc(
          merged,
          loadPointItemNoteLoadedGroupIds(workspaceId, currentGradeId)
        );
        return merged;
      });
      setGroupDataReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [
    gradeReady,
    workspaceId,
    currentGradeId,
    selectedGroupId,
    allMembers,
    loading,
    isPresentation,
  ]);

  const allPointTypes = useMemo(
    () => [...doc.pointSet.positiveTypes, ...doc.pointSet.negativeTypes],
    [doc.pointSet]
  );

  const selectedPointType = useMemo(
    () => allPointTypes.find((t) => t.id === selectedPointTypeId) ?? null,
    [allPointTypes, selectedPointTypeId]
  );

  const hasStoreDrafts = useMemo(
    () => Object.values(draftStorePurchases).some((arr) => arr.length > 0),
    [draftStorePurchases]
  );

  /** 스토어 '프로필 카드 구입' 탭에 노출할 세트: 워크스페이스에서 활성화된 것만 */
  const storeProfileCardSets: StoreProfileCardSet[] = useMemo(
    () =>
      profileCardSets
        .filter((s) => s.enabled)
        .map((s) => ({
          id: s.id,
          name: s.name,
          sortOrder: s.sortOrder,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          slots: s.slots,
          priceCoins: s.priceCoins,
          source: s.source,
        })),
    [profileCardSets]
  );

  /** 기록모드 카드 입력(draft)만으로도 저장 가능해야 함 */
  const hasFlushableDrafts = useMemo(() => {
    if (hasStoreDrafts) return true;
    const hasMemo = Object.values(draftMemos).some((t) => (t ?? '').trim().length > 0);
    if (displayMode === 'notes') return hasMemo;
    if (displayMode === 'items') return false;
    const hasValue = Object.values(draftValues).some((v) => typeof v === 'number' && v !== 0);
    return hasValue || hasMemo;
  }, [displayMode, draftMemos, draftValues, hasStoreDrafts]);

  const docDirty =
    baselineRef.current !== '' && snapshotDoc(doc) !== baselineRef.current;

  /** draft 입력이 있으면 baseline 유무와 관계없이 저장 활성화 */
  const isDirty =
    gradeReady && groupDataReady && (docDirty || hasFlushableDrafts || profileLayoutDirty);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (!gradeReady) return undefined;
    return subscribePointItemNoteDoc(({ doc: remote }) => {
      if (remote.workspaceId !== workspaceId || remote.gradeId !== currentGradeId) return;
      if (isDirtyRef.current) return;
      setDoc((local) => {
        if (
          !shouldApplyRemotePointItemNote({
            remote,
            local,
            isDirty: false,
          })
        ) {
          return local;
        }
        baselineRef.current = snapshotDoc(remote);
        setLivePointItemNoteDoc(
          remote,
          loadPointItemNoteLoadedGroupIds(remote.workspaceId, remote.gradeId)
        );
        return remote;
      });
    });
  }, [gradeReady, workspaceId, currentGradeId]);

  /** 자리 모드: 상하좌우 바깥 여백만 trim, 블럭 사이 빈 행·열은 압축 유지 */
  const seatViewData = useMemo(() => {
    const seated = members.filter((m) => m.seatPosition);
    const unseated = members.filter((m) => !m.seatPosition);
    if (!seated.length) {
      return {
        positioned: [] as Array<{ member: FlatMember; gridRow: number; gridCol: number }>,
        unseated,
        rowTemplate: '',
        colTemplate: '',
      };
    }

    const GRID_TRACKS = 16;
    const ROTATE_MAX_INDEX = 14;
    const usedRows = new Set<number>();
    const usedCols = new Set<number>();

    const rotatedSeats = seated.map((member) => {
      const seatRow = member.seatPosition!.row;
      const seatCol = member.seatPosition!.col;
      const rotatedRow = isSeatRotated ? ROTATE_MAX_INDEX - seatRow : seatRow;
      const rotatedCol = isSeatRotated ? ROTATE_MAX_INDEX - seatCol : seatCol;
      [rotatedRow, rotatedRow + 1].forEach((r) => {
        if (r >= 0 && r < GRID_TRACKS) usedRows.add(r);
      });
      [rotatedCol, rotatedCol + 1].forEach((c) => {
        if (c >= 0 && c < GRID_TRACKS) usedCols.add(c);
      });
      return { member, rotatedRow, rotatedCol };
    });

    const trimStartRow = Math.min(...Array.from(usedRows));
    const trimEndRow = Math.max(...Array.from(usedRows));
    const trimStartCol = Math.min(...Array.from(usedCols));
    const trimEndCol = Math.max(...Array.from(usedCols));

    const rowTemplate = Array.from(
      { length: Math.max(1, trimEndRow - trimStartRow + 1) },
      (_, idx) => (usedRows.has(trimStartRow + idx) ? '1fr' : '0.5fr')
    ).join(' ');

    // 사용 열=1fr, 빈 통로 열은 더 좁게 (중간 빈 열이 과도하게 넓어지지 않도록)
    const colTemplate = Array.from(
      { length: Math.max(1, trimEndCol - trimStartCol + 1) },
      (_, idx) => (usedCols.has(trimStartCol + idx) ? '1fr' : '0.28fr')
    ).join(' ');

    const positioned = rotatedSeats.map(({ member, rotatedRow, rotatedCol }) => ({
      member,
      gridRow: rotatedRow - trimStartRow + 1,
      gridCol: rotatedCol - trimStartCol + 1,
    }));

    return { positioned, unseated, rowTemplate, colTemplate };
  }, [members, isSeatRotated]);

  const hasSeatedMembers = seatViewData.positioned.length > 0;

  const enterSeatMode = useCallback(() => {
    if (!hasSeatedMembers) {
      showInfo(SEAT_EMPTY_TOAST);
      setLayoutMode('number');
      return;
    }
    setLayoutMode('seat');
    saveNoteMemberLayoutMode('pointItem', workspaceId, currentGradeId, 'seat');
  }, [hasSeatedMembers, showInfo, workspaceId, currentGradeId]);

  const setNumberLayoutMode = useCallback(() => {
    setLayoutMode('number');
    saveNoteMemberLayoutMode('pointItem', workspaceId, currentGradeId, 'number');
  }, [workspaceId, currentGradeId]);

  const setListLayoutMode = useCallback(() => {
    setLayoutMode('list');
    saveNoteMemberLayoutMode('pointItem', workspaceId, currentGradeId, 'list');
  }, [workspaceId, currentGradeId]);

  const applyLayoutMode = useCallback(
    (mode: LayoutMode) => {
      if (mode === 'seat') {
        enterSeatMode();
        return;
      }
      if (mode === 'list') {
        setListLayoutMode();
        return;
      }
      setNumberLayoutMode();
    },
    [enterSeatMode, setListLayoutMode, setNumberLayoutMode]
  );

  useEffect(() => {
    if (!isPresentation) return;
    if (displayMode === 'notes') setDisplayModeAndPersist('points');
  }, [isPresentation, displayMode, setDisplayModeAndPersist]);

  useEffect(() => {
    if (layoutMode !== 'seat') return;
    if (hasSeatedMembers) return;
    setLayoutMode('number');
    if (workspaceId && currentGradeId) {
      saveNoteMemberLayoutMode('pointItem', workspaceId, currentGradeId, 'number');
    }
    showInfo(SEAT_EMPTY_TOAST);
  }, [layoutMode, hasSeatedMembers, showInfo, workspaceId, currentGradeId]);

  const clearDrafts = useCallback(() => {
    setDraftValues({});
    setDraftMemos({});
    setDraftNoteTypes({});
    setDraftPointTypeIds({});
    setDraftStorePurchases({});
  }, []);

  const resolvePointMeta = useCallback(
    (typeId: string | null | undefined, value: number) => {
      const type =
        typeId === 'positive-default' || typeId === 'negative-default' || !typeId
          ? null
          : allPointTypes.find((t) => t.id === typeId) ?? null;
      const pointCategory: 'positive' | 'negative' =
        typeId === 'positive-default'
          ? 'positive'
          : typeId === 'negative-default'
            ? 'negative'
            : type?.category ?? (value >= 0 ? 'positive' : 'negative');
      return {
        type,
        pointTypeId: type?.id ?? typeId ?? '',
        pointCategory,
        typeMemo: type?.memo ?? '',
      };
    },
    [allPointTypes]
  );

  /** 카드·일괄반영 draft → 멤버 이력(기록날짜 / 기록유형·값 / 메모)으로 flush */
  const applyDraftsToDoc = useCallback(
    (base: PointItemNoteDoc): PointItemNoteDoc | null => {
      if (!selectedGroupId || !hasFlushableDrafts) return null;
      const now = new Date().toISOString();
      const date = selectedDateIso;
      const timeLabel = formatScheduleTimeTagLabel(selectedTimeTag);
      const tag =
        timeLabel !== '선택 안됨'
          ? timeLabel
          : buildHalfHourCustomTimeTagLabelForRecord({
              recordedDate: date,
              createdAt: now,
            });
      const prevG = base.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
      // assignmentPoints/Totals 유지 — 누락 시 저장 후 과제 합산이 0으로 사라짐
      let g: GroupMemberPoints = {
        ...prevG,
        totals: { ...prevG.totals },
        records: [...prevG.records],
        coinTotals: { ...prevG.coinTotals },
        coinRecords: [...prevG.coinRecords],
        notes: [...prevG.notes],
        rewardedLevels: { ...(prevG.rewardedLevels ?? {}) },
        itemInstances: [...(prevG.itemInstances ?? [])],
        assignmentPoints: [...(prevG.assignmentPoints ?? [])],
        assignmentTotals: { ...(prevG.assignmentTotals ?? {}) },
      };
      let changed = false;
      const oldTotalsSnapshot: Record<string, number> = {};

      Object.entries(draftStorePurchases).forEach(([memberId, purchases]) => {
        if (!purchases.length) return;
        for (const p of purchases) {
          const cost = Math.max(0, p.cost);
          const bal = g.coinTotals[memberId] ?? 0;
          if (cost > 0 && bal < cost) continue;
          if (cost > 0) {
            g.coinRecords = [
              {
                id: newId('coin'),
                memberId,
                coinValue: -cost,
                coinType: 'Store',
                memo: `${p.name || '아이템'} 스토어 카드 구입`,
                recordedDate: date,
                timeTagLabel: tag,
                createdAt: now,
              },
              ...g.coinRecords,
            ];
            g.coinTotals[memberId] = bal - cost;
          }
          const card = findItemCard(base.itemCards, p.itemCardId);
          g.itemInstances = [
            {
              id: newId('item'),
              memberId,
              itemCardId: p.itemCardId,
              category: 'store',
              remainingUseCount:
                (card?.type ?? p.type) === 'active'
                  ? Math.max(1, card?.useCount ?? p.useCount ?? 1)
                  : undefined,
              isLocked: false,
              acquiredAt: now,
            },
            ...(g.itemInstances ?? []),
          ];
          changed = true;
        }
      });

      if (displayMode === 'notes') {
        Object.entries(draftMemos).forEach(([memberId, raw]) => {
          const text = (raw ?? '').trim();
          if (!text) return;
          g.notes = [
            {
              id: newId('note'),
              memberId,
              text,
              noteType: draftNoteTypes[memberId] ?? '일반',
              recordedDate: date,
              timeTagLabel: tag,
              createdAt: now,
            },
            ...g.notes,
          ];
          changed = true;
        });
      } else if (displayMode === 'coins') {
        Object.entries(draftValues).forEach(([memberId, value]) => {
          if (!value) return;
          const memo = (draftMemos[memberId] ?? '').trim();
          g.coinRecords = [
            {
              id: newId('coin'),
              memberId,
              coinValue: value,
              coinType: 'General',
              memo: memo || undefined,
              recordedDate: date,
              timeTagLabel: tag,
              createdAt: now,
            },
            ...g.coinRecords,
          ];
          g.coinTotals[memberId] = (g.coinTotals[memberId] ?? 0) + value;
          changed = true;
        });
      } else if (displayMode === 'points') {
        Object.entries(draftValues).forEach(([memberId, value]) => {
          if (!value) return;
          const typeId = draftPointTypeIds[memberId] ?? selectedPointTypeId;
          const meta = resolvePointMeta(typeId, value);
          const memo = (draftMemos[memberId] ?? '').trim() || meta.typeMemo;
          oldTotalsSnapshot[memberId] = g.totals[memberId] ?? 0;
          g.records = [
            {
              id: newId('pr'),
              memberId,
              pointTypeId: meta.pointTypeId,
              pointValue: value,
              pointMemo: memo,
              pointCategory: meta.pointCategory,
              recordedDate: date,
              timeTagLabel: tag,
              createdAt: now,
            },
            ...g.records,
          ];
          g.totals[memberId] = (g.totals[memberId] ?? 0) + value;
          changed = true;
        });
        if (Object.keys(oldTotalsSnapshot).length > 0) {
          g = syncLevelUpRewardsForMembers(g, oldTotalsSnapshot, {
            levelSettings: base.levelSettings,
            itemCards: base.itemCards,
            recordedDate: date,
          });
        }
      }

      if (!changed) return null;
      const nextDoc: PointItemNoteDoc = {
        ...base,
        memberPointsByGroup: {
          ...base.memberPointsByGroup,
          [selectedGroupId]: g,
        },
        updatedAt: now,
      };
      if (displayMode === 'points' && Object.keys(oldTotalsSnapshot).length > 0) {
        return applyClassPointLocksToDoc(nextDoc);
      }
      return nextDoc;
    },
    [
      selectedGroupId,
      hasFlushableDrafts,
      selectedDateIso,
      selectedTimeTag,
      displayMode,
      draftMemos,
      draftNoteTypes,
      draftValues,
      draftPointTypeIds,
      draftStorePurchases,
      selectedPointTypeId,
      resolvePointMeta,
      applyClassPointLocksToDoc,
    ]
  );

  const handleSave = useCallback(async () => {
    if (!gradeReady || !groupDataReady) return;
    const flushed = applyDraftsToDoc(docRef.current);
    if (!flushed && hasFlushableDrafts && displayMode !== 'notes') {
      showError(
        displayMode === 'coins'
          ? '코인 값을 입력한 뒤 저장하세요.'
          : displayMode === 'items'
            ? '구매할 아이템을 선택한 뒤 저장하세요.'
            : '포인트 값을 입력한 뒤 저장하세요.'
      );
      return;
    }
    if (!flushed && !docDirty && !profileLayoutDirty) return;
    setSaving(true);
    showSaving();
    try {
      let current = flushed ?? docRef.current;
      let classPointLocked = false;
      if (flushed) {
        if ((displayMode === 'points' || displayMode === 'coins') && selectedGroupId) {
          const key = displayMode === 'points' ? 'totals' : 'coinTotals';
          const prevT = docRef.current.memberPointsByGroup[selectedGroupId]?.[key] ?? {};
          const nextT = flushed.memberPointsByGroup[selectedGroupId]?.[key] ?? {};
          const deltas: Record<string, number> = {};
          Object.keys(nextT).forEach((id) => {
            const d = (nextT[id] ?? 0) - (prevT[id] ?? 0);
            if (d) deltas[id] = d;
          });
          showGainFloats(displayMode, deltas);
        }
        setDoc(flushed);
        docRef.current = flushed;
        clearDrafts();
        setSelectedMemberIds(new Set());
      }
      {
        const before = current;
        current = applyClassPointLocksToDoc(current);
        if (current !== before) {
          classPointLocked = true;
          setDoc(current);
          docRef.current = current;
        }
      }
      const saves: Promise<unknown>[] = [];
      if (flushed || docDirty || classPointLocked) {
        const lockGroupIds = classPointLocked
          ? (() => {
              const cp = current.classPointSettings ?? emptyClassPointSettings();
              return cp.integrateAllGroups
                ? classPointGroupScopes.map((g) => g.groupId)
                : [selectedGroupId];
            })()
          : [selectedGroupId];
        saves.push(
          savePointItemNote(current, {
            saveGroups: buildSaveGroups(lockGroupIds),
          })
        );
      }
      if (profileLayoutDirty) {
        const layout = buildLayoutFromMembers(groups, allMembers);
        cacheClassMemberGradeLayout(workspaceId, currentGradeId, layout);
        saves.push(replaceGradeMemberLayout(workspaceId, currentGradeId, layout));
      }
      await Promise.all(saves);
      if (flushed || docDirty || classPointLocked) {
        baselineRef.current = snapshotDoc(current);
      }
      if (profileLayoutDirty) setProfileLayoutDirty(false);
      showSuccess(
        displayMode === 'items' ? '구매가 반영되었습니다.' : '저장되었습니다.'
      );
    } catch (e) {
      showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [
    gradeReady,
    groupDataReady,
    applyDraftsToDoc,
    hasFlushableDrafts,
    displayMode,
    docDirty,
    profileLayoutDirty,
    clearDrafts,
    showError,
    showSaving,
    showSuccess,
    selectedGroupId,
    members,
    classPointGroupScopes,
    applyClassPointLocksToDoc,
    buildSaveGroups,
    groups,
    allMembers,
    workspaceId,
    currentGradeId,
    showGainFloats,
  ]);

  const handleReset = () => {
    if (!baselineRef.current) return;
    try {
      const parsed = JSON.parse(baselineRef.current) as Pick<
        PointItemNoteDoc,
        'pointSet' | 'itemCards' | 'levelSettings' | 'classPointSettings' | 'memberPointsByGroup'
      >;
      setDoc((prev) => ({
        ...prev,
        ...parsed,
        updatedAt: new Date().toISOString(),
      }));
      clearDrafts();
      setSelectedMemberIds(new Set());
      if (profileLayoutDirty) {
        const layout =
          loadClassMemberGradeLayoutFromStorage(workspaceId, currentGradeId) ?? emptyGradeLayout();
        const nextGroups = [...layout.groups].sort(
          (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
        );
        setGroups(nextGroups);
        setAllMembers(flattenMembers(layout));
        setProfileLayoutDirty(false);
      }
    } catch {
      void load();
    }
  };

  const persistSettingsDoc = async (
    nextDoc: PointItemNoteDoc,
    successMessage?: string
  ) => {
    setDoc(nextDoc);
    docRef.current = nextDoc;
    try {
      showSaving();
      // 설정만 저장 — 멤버 테이블은 건드리지 않음(다른 그룹 보호)
      await savePointItemNote(nextDoc);
      baselineRef.current = snapshotDoc(nextDoc);
      if (successMessage) showSuccess(successMessage);
    } catch (e) {
      showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    }
  };

  const updatePointSet = (pointSet: PointTypeSet) => {
    const prev = docRef.current;
    const nextDoc: PointItemNoteDoc = {
      ...prev,
      pointSet,
      levelSettings: {
        ...prev.levelSettings,
        levelUpPoints: pointSet.levelUpPoints,
      },
      updatedAt: new Date().toISOString(),
    };
    void persistSettingsDoc(nextDoc, '포인트 세트가 저장되었습니다.');
  };

  const updateItemCards = (itemCards: ItemCardSet) => {
    const nextDoc: PointItemNoteDoc = {
      ...docRef.current,
      itemCards,
      updatedAt: new Date().toISOString(),
    };
    void persistSettingsDoc(nextDoc, '아이템 카드가 저장되었습니다.');
  };

  const updateItemCardImages = (
    itemCardImages: ItemCardUserImage[],
    options?: { silent?: boolean }
  ) => {
    const nextDoc: PointItemNoteDoc = {
      ...docRef.current,
      itemCardImages,
      updatedAt: new Date().toISOString(),
    };
    // 선택 모달에서 라이브러리 편집 중에는 토스트·저장중 표시 없이 조용히 반영
    if (options?.silent) {
      setDoc(nextDoc);
      docRef.current = nextDoc;
      void (async () => {
        try {
          await savePointItemNote(nextDoc);
          baselineRef.current = snapshotDoc(nextDoc);
        } catch (e) {
          showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
        }
      })();
      return;
    }
    void persistSettingsDoc(nextDoc, '아이템 카드 이미지가 저장되었습니다.');
  };

  const updateLevelSettings = (newLevelSettings: PointLevelSettings) => {
    const prev = docRef.current;
    const prevSettings = prev.levelSettings;
    if (JSON.stringify(prevSettings) === JSON.stringify(newLevelSettings)) {
      setLevelSettingsOpen(false);
      return;
    }
    const updatedAt = new Date().toISOString();

    // 전 그룹의 멤버 보상 재조정
    const nextGroups = { ...prev.memberPointsByGroup };
    const saveGroupIds: string[] = [];
    Object.entries(nextGroups).forEach(([gid, group]) => {
      const reconciled = reconcileLevelRewardsOnSettingsChange(
        group,
        prevSettings,
        newLevelSettings,
        prev.itemCards
      );
      if (reconciled !== group) {
        nextGroups[gid] = reconciled;
        saveGroupIds.push(gid);
      }
    });

    const nextDoc: PointItemNoteDoc = {
      ...prev,
      levelSettings: newLevelSettings,
      pointSet: {
        ...prev.pointSet,
        levelUpPoints: newLevelSettings.levelUpPoints,
      },
      memberPointsByGroup: nextGroups,
      updatedAt,
    };

    if (saveGroupIds.length > 0) {
      const saveGroups: PointItemNoteSaveGroup[] = saveGroupIds.map((gid) => ({
        groupId: gid,
        memberIds: Object.keys(nextGroups[gid]?.totals ?? {}),
      }));
      docRef.current = nextDoc;
      baselineRef.current = snapshotDoc(nextDoc);
      setDoc(nextDoc);
      void (async () => {
        try {
          await savePointItemNote(nextDoc, { saveGroups });
          showSuccess('레벨 설정 및 보상이 저장되었습니다.');
        } catch (e) {
          showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
        }
      })();
    } else {
      void persistSettingsDoc(nextDoc, '레벨 설정이 저장되었습니다.');
    }
  };

  const updateClassPointSettings = (classPointSettings: ClassPointSettings) => {
    let nextDoc: PointItemNoteDoc = {
      ...docRef.current,
      classPointSettings,
      updatedAt: new Date().toISOString(),
    };
    let saveGroupIds: Array<string | null | undefined> = [];
    if (selectedGroupId && (members.length > 0 || classPointSettings.integrateAllGroups)) {
      const classTotal = computeClassPointTotal(
        nextDoc.memberPointsByGroup,
        classPointSettings,
        {
          currentGroupId: selectedGroupId,
          currentMemberIds: members.map((m) => m.id),
          allGroups: classPointGroupScopes,
        }
      );
      const synced = reconcileClassPointCardLocks(
        nextDoc.memberPointsByGroup,
        classPointGroupScopes,
        classPointSettings,
        { currentGroupId: selectedGroupId, classTotal }
      );
      if (synced.touchedGroupIds.length > 0) {
        nextDoc = {
          ...nextDoc,
          memberPointsByGroup: synced.memberPointsByGroup,
        };
        saveGroupIds = synced.touchedGroupIds;
      }
    }
    setDoc(nextDoc);
    docRef.current = nextDoc;
    void (async () => {
      try {
        showSaving();
        await savePointItemNote(
          nextDoc,
          saveGroupIds.length > 0
            ? { saveGroups: buildSaveGroups(saveGroupIds) }
            : undefined
        );
        baselineRef.current = snapshotDoc(nextDoc);
        showSuccess('학급 포인트 설정이 저장되었습니다.');
      } catch (e) {
        showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
      }
    })();
  };

  const handleClassPointClaim = useCallback(async () => {
    if (!selectedGroupId) return;
    const settings = docRef.current.classPointSettings ?? emptyClassPointSettings();
    if (!settings.enabled) return;
    const classTotal = computeClassPointTotal(
      docRef.current.memberPointsByGroup,
      settings,
      {
        currentGroupId: selectedGroupId,
        currentMemberIds: members.map((m) => m.id),
        allGroups: classPointGroupScopes,
      }
    );
    const claimable = getClaimableClassPointTiers(
      docRef.current.memberPointsByGroup,
      classPointGroupScopes,
      settings,
      { currentGroupId: selectedGroupId, classTotal }
    );
    if (claimable.length === 0) return;

    const scopeKey = settings.integrateAllGroups
      ? '__all_groups__'
      : selectedGroupId;
    const already = new Set(settings.grantedTierByGroup[scopeKey] ?? []);
    const unlockOnly = claimable.every((t) => already.has(t.id));
    const cards = claimable.flatMap((t) => t.cards);

    const synced = claimClassPointRewards(
      docRef.current.memberPointsByGroup,
      classPointGroupScopes,
      settings,
      { currentGroupId: selectedGroupId, classTotal }
    );
    if (synced.newlyGrantedTierIds.length === 0) return;

    const nextDoc: PointItemNoteDoc = {
      ...docRef.current,
      classPointSettings: synced.settings,
      memberPointsByGroup: synced.memberPointsByGroup,
      updatedAt: new Date().toISOString(),
    };
    setDoc(nextDoc);
    docRef.current = nextDoc;
    setClassPointClaimReveal({
      cards,
      mode: unlockOnly ? 'unlock' : 'grant',
    });
    try {
      showSaving();
      await savePointItemNote(nextDoc, {
        saveGroups: buildSaveGroups(synced.touchedGroupIds),
      });
      baselineRef.current = snapshotDoc(nextDoc);
      showSuccess(
        unlockOnly
          ? '학급 포인트 카드 잠금이 해제되었습니다.'
          : '학급 포인트 보상이 지급되었습니다.'
      );
    } catch (e) {
      showError(e instanceof Error ? e.message : '보상 지급에 실패했습니다.');
    }
  }, [
    selectedGroupId,
    members,
    classPointGroupScopes,
    buildSaveGroups,
    showSaving,
    showSuccess,
    showError,
  ]);

  const toggleMember = (id: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedMemberIds(new Set(members.map((m) => m.id)));
  const clearSelection = () => setSelectedMemberIds(new Set());

  /** 전체 기록 모드: 1명 이상 / 개별 기록 모드: 프로필 클릭으로 2명 이상 선택 시 일괄 지급 */
  const bulkEnabled = isRecordMode
    ? selectedMemberIds.size > 0
    : selectedMemberIds.size >= 2;

  const openBulkModal = () => {
    if (!bulkEnabled) return;
    if (displayMode === 'items') {
      setItemGrantTargetIds(Array.from(selectedMemberIds));
      setItemGrantOpen(true);
      return;
    }
    if (displayMode === 'points') {
      setBulkPointTypeId(selectedPointTypeId ?? '');
      setBulkPointValue(selectedPointType ? String(selectedPointType.points) : '');
      setBulkPointMemo(selectedPointType?.memo ?? '');
    } else if (displayMode === 'coins') {
      setBulkCoinAmount(10);
      setBulkCoinMemo('');
    } else {
      setBulkNoteText('');
      setBulkNoteType('일반');
    }
    setBulkModalOpen(true);
  };

  const applyBulkToBlocks = () => {
    if (selectedMemberIds.size === 0) return;
    const ids = Array.from(selectedMemberIds);

    if (displayMode === 'points') {
      const value = Number.parseInt(bulkPointValue, 10) || 0;
      const typeId = bulkPointTypeId || undefined;
      setDraftPointTypeIds((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (typeId) next[id] = typeId;
        });
        return next;
      });
      setDraftValues((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = value;
        });
        return next;
      });
      setDraftMemos((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = bulkPointMemo;
        });
        return next;
      });
      if (typeId && typeId !== 'positive-default' && typeId !== 'negative-default') {
        setSelectedPointTypeId(typeId);
      }
    } else if (displayMode === 'coins') {
      setDraftValues((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = bulkCoinAmount;
        });
        return next;
      });
      setDraftMemos((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = bulkCoinMemo;
        });
        return next;
      });
    } else {
      setDraftMemos((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = bulkNoteText;
        });
        return next;
      });
      setDraftNoteTypes((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = bulkNoteType;
        });
        return next;
      });
    }
    setBulkModalOpen(false);
  };

  const grantFromBulkModal = () => {
    if (!selectedGroupId || selectedMemberIds.size === 0) return;
    const now = new Date().toISOString();
    const date = selectedDateIso;
    const timeLabel = formatScheduleTimeTagLabel(selectedTimeTag);
    const tag =
      timeLabel !== '선택 안됨'
        ? timeLabel
        : buildHalfHourCustomTimeTagLabelForRecord({
            recordedDate: date,
            createdAt: now,
          });
    const ids = Array.from(selectedMemberIds);

    if (displayMode === 'notes') {
      const text = bulkNoteText.trim();
      if (!text) return;
      const notes: MemberNoteRecord[] = ids.map((memberId) => ({
        id: newId('note'),
        memberId,
        text,
        noteType: bulkNoteType,
        recordedDate: date,
        timeTagLabel: tag,
        createdAt: now,
      }));
      setDoc((prev) => {
        const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
        return {
          ...prev,
          memberPointsByGroup: {
            ...prev.memberPointsByGroup,
            [selectedGroupId]: { ...g, notes: [...notes, ...g.notes] },
          },
          updatedAt: now,
        };
      });
    } else if (displayMode === 'coins') {
      if (bulkCoinAmount === 0) return;
      const coinRecords: MemberCoinRecord[] = ids.map((memberId) => ({
        id: newId('coin'),
        memberId,
        coinValue: bulkCoinAmount,
        coinType: 'General',
        memo: bulkCoinMemo.trim() || undefined,
        recordedDate: date,
        timeTagLabel: tag,
        createdAt: now,
      }));
      setDoc((prev) => {
        const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
        const coinTotals = { ...g.coinTotals };
        ids.forEach((mid) => {
          coinTotals[mid] = (coinTotals[mid] ?? 0) + bulkCoinAmount;
        });
        return {
          ...prev,
          memberPointsByGroup: {
            ...prev.memberPointsByGroup,
            [selectedGroupId]: {
              ...g,
              coinTotals,
              coinRecords: [...coinRecords, ...g.coinRecords],
            },
          },
          updatedAt: now,
        };
      });
    } else {
      const value = Number.parseInt(bulkPointValue, 10) || 0;
      if (value === 0) return;
      const meta = resolvePointMeta(bulkPointTypeId, value);
      const memo = bulkPointMemo.trim() || meta.typeMemo;
      const records: MemberPointRecord[] = ids.map((memberId) => ({
        id: newId('pr'),
        memberId,
        pointTypeId: meta.pointTypeId,
        pointValue: value,
        pointMemo: memo,
        pointCategory: meta.pointCategory,
        recordedDate: date,
        timeTagLabel: tag,
        createdAt: now,
      }));
      setDoc((prev) => {
        const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
        const oldTotals: Record<string, number> = {};
        const totals = { ...g.totals };
        ids.forEach((mid) => {
          oldTotals[mid] = totals[mid] ?? 0;
          totals[mid] = (totals[mid] ?? 0) + value;
        });
        let nextG: GroupMemberPoints = {
          ...g,
          totals,
          records: [...records, ...g.records],
        };
        nextG = syncLevelUpRewardsForMembers(nextG, oldTotals, {
          levelSettings: prev.levelSettings,
          itemCards: prev.itemCards,
          recordedDate: date,
        });
        const nextDoc: PointItemNoteDoc = {
          ...prev,
          memberPointsByGroup: {
            ...prev.memberPointsByGroup,
            [selectedGroupId]: nextG,
          },
          updatedAt: now,
        };
        return applyClassPointLocksToDoc(nextDoc);
      });
    }

    setDraftValues((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = 0;
      });
      return next;
    });
    setDraftMemos((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = '';
      });
      return next;
    });
    setBulkModalOpen(false);
  };

  /** 탭(기록/코인/포인트)의 지정 기록 1건을 문서에서 제거 — id 지정이 없으면 최신 기록 */
  const removeTabRecordFromDoc = useCallback(
    (
      d: PointItemNoteDoc,
      mode: DisplayMode,
      groupId: string,
      targetId?: string
    ): { next: PointItemNoteDoc; removedId: string } | null => {
      const g = d.memberPointsByGroup[groupId] ?? emptyGroupMemberPoints();
      const stamp = new Date().toISOString();
      if (mode === 'notes') {
        const idx = targetId ? g.notes.findIndex((r) => r.id === targetId) : 0;
        const target = g.notes[idx];
        if (idx < 0 || !target) return null;
        const notes = g.notes.filter((_, k) => k !== idx);
        return {
          removedId: target.id,
          next: {
            ...d,
            memberPointsByGroup: { ...d.memberPointsByGroup, [groupId]: { ...g, notes } },
            updatedAt: stamp,
          },
        };
      }
      if (mode === 'coins') {
        const idx = targetId ? g.coinRecords.findIndex((r) => r.id === targetId) : 0;
        const target = g.coinRecords[idx];
        if (idx < 0 || !target) return null;
        const coinRecords = g.coinRecords.filter((_, k) => k !== idx);
        const coinTotals = { ...g.coinTotals };
        coinTotals[target.memberId] = (coinTotals[target.memberId] ?? 0) - target.coinValue;
        return {
          removedId: target.id,
          next: {
            ...d,
            memberPointsByGroup: {
              ...d.memberPointsByGroup,
              [groupId]: { ...g, coinTotals, coinRecords },
            },
            updatedAt: stamp,
          },
        };
      }
      if (mode !== 'points') return null;
      const idx = targetId ? g.records.findIndex((r) => r.id === targetId) : 0;
      const target = g.records[idx];
      if (idx < 0 || !target) return null;
      const records = g.records.filter((_, k) => k !== idx);
      const oldTotals = { [target.memberId]: g.totals[target.memberId] ?? 0 };
      const totals = { ...g.totals };
      totals[target.memberId] = (totals[target.memberId] ?? 0) - target.pointValue;
      let nextG: GroupMemberPoints = { ...g, totals, records };
      nextG = syncLevelUpRewardsForMembers(nextG, oldTotals, {
        levelSettings: d.levelSettings,
        itemCards: d.itemCards,
      });
      return {
        removedId: target.id,
        next: applyClassPointLocksToDoc({
          ...d,
          memberPointsByGroup: { ...d.memberPointsByGroup, [groupId]: nextG },
          updatedAt: stamp,
        }),
      };
    },
    [applyClassPointLocksToDoc]
  );

  const undoingRef = useRef(false);
  const [undoing, setUndoing] = useState(false);

  /**
   * 마지막 지급 취소 — 현재 탭의 최신 "저장된" 기록 1건을 지우고 즉시 DB 저장.
   * 마지막 저장본(baseline)에 적용해 저장하므로 다른 탭의 미저장 입력·문서 변경은 포함되지 않음.
   * 화면 문서에는 같은 기록만 제거해 병합(다른 미저장 변경 유지).
   */
  const undoLast = () => {
    if (!selectedGroupId || undoingRef.current || saving) return;
    if (!baselineRef.current) return;
    const groupId = selectedGroupId;
    const mode = displayMode;
    let base: PointItemNoteDoc;
    try {
      base = {
        ...docRef.current,
        ...(JSON.parse(baselineRef.current) as Partial<PointItemNoteDoc>),
      };
    } catch {
      return;
    }
    const saved = removeTabRecordFromDoc(base, mode, groupId);
    if (!saved) {
      showInfo('취소할 저장된 기록이 없습니다.');
      return;
    }
    const prevBaseline = baselineRef.current;
    const liveBefore = docRef.current;
    const live = removeTabRecordFromDoc(liveBefore, mode, groupId, saved.removedId);
    const liveNext = live ? live.next : liveBefore;
    setDoc(liveNext);
    docRef.current = liveNext;
    // 화면의 다른 미저장 변경 판정이 깨지지 않도록 즉시 baseline 갱신
    baselineRef.current = snapshotDoc(saved.next);
    undoingRef.current = true;
    setUndoing(true);
    void (async () => {
      try {
        await savePointItemNote(saved.next, { saveGroups: buildSaveGroups([groupId]) });
      } catch (e) {
        baselineRef.current = prevBaseline;
        // 실패 시 화면 복원 — 그 사이 다른 변경이 없을 때만 통째 복원
        setDoc((cur) => (cur === liveNext ? liveBefore : cur));
        showError(e instanceof Error ? e.message : '마지막 지급 취소 저장에 실패했습니다.');
      } finally {
        undoingRef.current = false;
        setUndoing(false);
      }
    })();
  };

  const noteCountsByMember = useMemo(() => {
    const map = new Map<string, Record<MemberNoteType, number>>();
    groupPoints.notes.forEach((n) => {
      const cur = map.get(n.memberId) ?? { 일반: 0, 학습: 0, 행동: 0 };
      const t: MemberNoteType =
        n.noteType === '학습' || n.noteType === '행동' ? n.noteType : '일반';
      cur[t] += 1;
      map.set(n.memberId, cur);
    });
    return map;
  }, [groupPoints.notes]);

  const historyMember = useMemo(
    () => (historyMemberId ? members.find((m) => m.id === historyMemberId) ?? null : null),
    [historyMemberId, members]
  );

  const historyRows = useMemo((): MemberHistoryRow[] => {
    if (!historyMemberId) return [];
    if (displayMode === 'notes') {
      return groupPoints.notes
        .filter((n) => n.memberId === historyMemberId)
        .map((n) => ({
          id: n.id,
          recordedDate: n.recordedDate,
          memo: n.text,
          typeLabel: n.noteType,
          timeTagLabel: resolveDisplayTimeTagLabel({
            timeTagLabel: n.timeTagLabel,
            recordedDate: n.recordedDate,
            createdAt: n.createdAt,
          }),
        }));
    }
    if (displayMode === 'coins') {
      return groupPoints.coinRecords
        .filter((r) => r.memberId === historyMemberId)
        .map((r) => ({
          id: r.id,
          recordedDate: r.recordedDate,
          memo: r.memo ?? '',
          value: r.coinValue,
          valueLabel: `${r.coinValue > 0 ? '+' : ''}${r.coinValue}`,
          coinTypeLabel: r.coinType === 'Levelup' ? 'L' : 'G',
          timeTagLabel: resolveDisplayTimeTagLabel({
            timeTagLabel: r.timeTagLabel,
            recordedDate: r.recordedDate,
            createdAt: r.createdAt,
          }),
        }));
    }
    return groupPoints.records
      .filter((r) => r.memberId === historyMemberId)
      .map((r) => {
        const type = allPointTypes.find((t) => t.id === r.pointTypeId);
        return {
          id: r.id,
          recordedDate: r.recordedDate,
          memo: r.pointMemo || '',
          pointCategory: r.pointCategory,
          pointEmoji: type?.emoji,
          value: r.pointValue,
          valueLabel: `${r.pointValue}점`,
          timeTagLabel: resolveDisplayTimeTagLabel({
            timeTagLabel: r.timeTagLabel,
            recordedDate: r.recordedDate,
            createdAt: r.createdAt,
          }),
        };
      });
  }, [historyMemberId, displayMode, groupPoints, allPointTypes]);

  const STATUS_LABEL: Record<string, string> = {
    pending: '미확인',
    confirmed: '확인',
    delayed: '지연',
    exempted: '예외',
    excluded: '제외',
    submitted: '제출',
  };

  /** 그룹 로드 시 이미 받은 assignmentPoints 재사용 — 모달 열 때 추가 fetch 없음 */
  const assignmentHistoryRows = useMemo((): MemberHistoryRow[] => {
    if (!historyMemberId || displayMode !== 'points') return [];
    return (groupPoints.assignmentPoints ?? [])
      .filter(
        (r) =>
          r.memberId === historyMemberId &&
          r.earnedPoints > 0 &&
          r.status !== 'excluded'
      )
      .map((r) => ({
        id: r.id,
        recordedDate: r.recordedDate,
        memo: r.memo?.trim() ? `${r.assignmentTitle} · ${r.memo.trim()}` : r.assignmentTitle,
        typeLabel: STATUS_LABEL[r.status] ?? r.status,
        assignmentKind: r.assignmentKind,
        value: r.earnedPoints,
        valueLabel: `${r.earnedPoints}점`,
        readOnly: true,
      }));
  }, [historyMemberId, displayMode, groupPoints.assignmentPoints]);

  const historyTitle =
    displayMode === 'coins' ? '코인 기록' : displayMode === 'notes' ? '노트 기록' : '포인트 기록';

  const openMemberHistory = (memberId: string) => {
    if (isRecordMode) return;
    setHistoryMemberId(memberId);
  };

  const openMemberItemInventory = (memberId: string) => {
    if (isRecordMode) return;
    setItemInventoryMemberId(memberId);
    // 프로필 카드는 뽑기 직후 DB에 바로 반영되는데, 그룹 전체 이력은 최초 1회만 불러와
    // 캐시에 고정되는 구조라 이후 변경분(예: 다른 화면에서의 뽑기)을 놓칠 수 있다.
    // 조회 모달을 열 때 이 멤버의 프로필 카드 보유 이력만 가볍게 다시 불러와 최신화한다.
    if (!selectedGroupId || !workspaceId || !currentGradeId) return;
    const gid = selectedGroupId;
    void fetchMemberProfileCardUnlocksFresh(workspaceId, currentGradeId, memberId)
      .then((fresh) => {
        const prev = docRef.current;
        const g = prev.memberPointsByGroup[gid];
        if (!g) return;
        const others = (g.profileCardUnlocks ?? []).filter((u) => u.memberId !== memberId);
        const nextDoc: PointItemNoteDoc = {
          ...prev,
          memberPointsByGroup: {
            ...prev.memberPointsByGroup,
            [gid]: { ...g, profileCardUnlocks: [...others, ...fresh] },
          },
        };
        setDoc(nextDoc);
        docRef.current = nextDoc;
        savePointItemNoteCache(nextDoc, { force: true });
      })
      .catch(() => {
        /* 최신화 실패 시 기존 캐시 표시 유지 */
      });
  };

  const itemInventoryMember = useMemo(() => {
    if (!itemInventoryMemberId) return null;
    const m = allMembers.find((x) => x.id === itemInventoryMemberId);
    if (!m) return null;
    return { id: m.id, orderIndex: m.orderIndex, name: displayClassMemberName(m) };
  }, [itemInventoryMemberId, allMembers]);

  const getHistoryTimeTagsForDate = useCallback(
    (dateIso: string) => {
      if (!gradeReady) return [];
      return getScheduleTimeTagsForGrade({
        workspaceId,
        gradeId: currentGradeId,
        grades,
        date: parseIsoDate(dateIso),
      });
    },
    [workspaceId, currentGradeId, grades, gradeReady]
  );

  const deleteHistoryRecords = useCallback(
    (ids: string[]) => {
      if (!selectedGroupId || ids.length === 0) return;
      const idSet = new Set(ids);
      setDoc((prev) => {
        const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
        const now = new Date().toISOString();
        if (displayMode === 'notes') {
          const notes = g.notes.filter((n) => !idSet.has(n.id));
          if (notes.length === g.notes.length) return prev;
          return {
            ...prev,
            memberPointsByGroup: {
              ...prev.memberPointsByGroup,
              [selectedGroupId]: { ...g, notes },
            },
            updatedAt: now,
          };
        }
        if (displayMode === 'coins') {
          const removed = g.coinRecords.filter((r) => idSet.has(r.id));
          if (!removed.length) return prev;
          const coinRecords = g.coinRecords.filter((r) => !idSet.has(r.id));
          const coinTotals = { ...g.coinTotals };
          removed.forEach((r) => {
            coinTotals[r.memberId] = (coinTotals[r.memberId] ?? 0) - r.coinValue;
          });
          return {
            ...prev,
            memberPointsByGroup: {
              ...prev.memberPointsByGroup,
              [selectedGroupId]: { ...g, coinRecords, coinTotals },
            },
            updatedAt: now,
          };
        }
        const removed = g.records.filter((r) => idSet.has(r.id));
        if (!removed.length) return prev;
        const records = g.records.filter((r) => !idSet.has(r.id));
        const oldTotals: Record<string, number> = {};
        const totals = { ...g.totals };
        removed.forEach((r) => {
          oldTotals[r.memberId] = totals[r.memberId] ?? 0;
          totals[r.memberId] = (totals[r.memberId] ?? 0) - r.pointValue;
        });
        let nextG: GroupMemberPoints = { ...g, records, totals };
        nextG = syncLevelUpRewardsForMembers(nextG, oldTotals, {
          levelSettings: prev.levelSettings,
          itemCards: prev.itemCards,
        });
        const nextDoc: PointItemNoteDoc = {
          ...prev,
          memberPointsByGroup: {
            ...prev.memberPointsByGroup,
            [selectedGroupId]: nextG,
          },
          updatedAt: now,
        };
        return applyClassPointLocksToDoc(nextDoc);
      });
    },
    [selectedGroupId, displayMode, applyClassPointLocksToDoc]
  );

  const updateHistoryRecords = useCallback(
    (edits: MemberHistoryRecordEdit[]) => {
      if (!selectedGroupId || edits.length === 0) return;
      const editMap = new Map(edits.map((e) => [e.id, e]));
      setDoc((prev) => {
        const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
        const now = new Date().toISOString();

        if (displayMode === 'notes') {
          let changed = false;
          const notes = g.notes.map((n) => {
            const edit = editMap.get(n.id);
            if (!edit) return n;
            const noteType: MemberNoteType =
              edit.typeLabel === '학습' || edit.typeLabel === '행동' ? edit.typeLabel : '일반';
            const next: MemberNoteRecord = {
              ...n,
              text: edit.memo,
              noteType,
              recordedDate: edit.recordedDate,
              timeTagLabel: edit.timeTagLabel?.trim() || undefined,
            };
            if (
              next.text !== n.text ||
              next.noteType !== n.noteType ||
              next.recordedDate !== n.recordedDate ||
              (next.timeTagLabel || '') !== (n.timeTagLabel || '')
            ) {
              changed = true;
            }
            return next;
          });
          if (!changed) return prev;
          return {
            ...prev,
            memberPointsByGroup: {
              ...prev.memberPointsByGroup,
              [selectedGroupId]: { ...g, notes },
            },
            updatedAt: now,
          };
        }

        if (displayMode === 'coins') {
          let changed = false;
          const coinTotals = { ...g.coinTotals };
          const coinRecords = g.coinRecords.map((r) => {
            const edit = editMap.get(r.id);
            if (!edit) return r;
            const nextValue = edit.value ?? r.coinValue;
            // 코인 유형(G/L)은 수정 불가 — 기존 값 유지
            const next = {
              ...r,
              memo: edit.memo.trim() || undefined,
              recordedDate: edit.recordedDate,
              timeTagLabel: edit.timeTagLabel?.trim() || undefined,
              coinValue: nextValue,
            };
            if (
              next.memo !== r.memo ||
              next.recordedDate !== r.recordedDate ||
              (next.timeTagLabel || '') !== (r.timeTagLabel || '') ||
              next.coinValue !== r.coinValue
            ) {
              changed = true;
              coinTotals[r.memberId] =
                (coinTotals[r.memberId] ?? 0) - r.coinValue + nextValue;
            }
            return next;
          });
          if (!changed) return prev;
          return {
            ...prev,
            memberPointsByGroup: {
              ...prev.memberPointsByGroup,
              [selectedGroupId]: { ...g, coinRecords, coinTotals },
            },
            updatedAt: now,
          };
        }

        let changed = false;
        const oldTotals: Record<string, number> = {};
        const totals = { ...g.totals };
        const records = g.records.map((r) => {
          const edit = editMap.get(r.id);
          if (!edit) return r;
          const nextValue = edit.value ?? r.pointValue;
          const nextCategory = edit.pointCategory ?? r.pointCategory;
          const next: MemberPointRecord = {
            ...r,
            pointMemo: edit.memo,
            recordedDate: edit.recordedDate,
            timeTagLabel: edit.timeTagLabel?.trim() || undefined,
            pointValue: nextValue,
            pointCategory: nextCategory,
          };
          if (
            next.pointMemo !== r.pointMemo ||
            next.recordedDate !== r.recordedDate ||
            (next.timeTagLabel || '') !== (r.timeTagLabel || '') ||
            next.pointValue !== r.pointValue ||
            next.pointCategory !== r.pointCategory
          ) {
            changed = true;
            if (oldTotals[r.memberId] == null) {
              oldTotals[r.memberId] = totals[r.memberId] ?? 0;
            }
            totals[r.memberId] = (totals[r.memberId] ?? 0) - r.pointValue + nextValue;
          }
          return next;
        });
        if (!changed) return prev;
        let nextG: GroupMemberPoints = { ...g, records, totals };
        if (Object.keys(oldTotals).length > 0) {
          nextG = syncLevelUpRewardsForMembers(nextG, oldTotals, {
            levelSettings: prev.levelSettings,
            itemCards: prev.itemCards,
          });
        }
        const nextDoc: PointItemNoteDoc = {
          ...prev,
          memberPointsByGroup: {
            ...prev.memberPointsByGroup,
            [selectedGroupId]: nextG,
          },
          updatedAt: now,
        };
        return Object.keys(oldTotals).length > 0
          ? applyClassPointLocksToDoc(nextDoc)
          : nextDoc;
      });
    },
    [selectedGroupId, displayMode, applyClassPointLocksToDoc]
  );

  const dayBadge = selectedTimeTag
    ? ({ mon: '월', tue: '화', wed: '수', thu: '목', fri: '금', sat: '토' } as Record<string, string>)[
        selectedTimeTag.dayKey
      ] ?? DAY_KO[selectedDate.getDay()]
    : DAY_KO[selectedDate.getDay()];

  const dateYear = selectedDate.getFullYear();
  const dateMonth = selectedDate.getMonth() + 1;
  const dateDay = selectedDate.getDate();

  const beginDateEdit = (field: 'year' | 'month' | 'day') => {
    setDateEditingField(field);
    if (field === 'year') setDateTempValue(String(dateYear));
    else if (field === 'month') setDateTempValue(String(dateMonth));
    else setDateTempValue(String(dateDay));
  };

  const commitDateEdit = (field: 'year' | 'month' | 'day') => {
    const value = Number.parseInt(dateTempValue, 10);
    if (!Number.isFinite(value)) {
      setDateEditingField(null);
      setDateTempValue('');
      return;
    }
    let y = dateYear;
    let m = dateMonth;
    let d = dateDay;
    if (field === 'year') {
      if (value < 2000 || value > 2100) {
        setDateEditingField(null);
        setDateTempValue('');
        return;
      }
      y = value;
    } else if (field === 'month') {
      if (value < 1 || value > 12) {
        setDateEditingField(null);
        setDateTempValue('');
        return;
      }
      m = value;
    } else if (value < 1 || value > 31) {
      setDateEditingField(null);
      setDateTempValue('');
      return;
    } else {
      d = value;
    }
    const next = new Date(y, m - 1, d);
    if (
      next.getFullYear() === y &&
      next.getMonth() === m - 1 &&
      next.getDate() === d
    ) {
      setSelectedDateIso(toIsoDate(next));
    }
    setDateEditingField(null);
    setDateTempValue('');
  };

  const bulkLabel =
    displayMode === 'coins'
      ? '일괄 코인 지급'
      : displayMode === 'notes'
        ? '일괄 기록 작성'
        : displayMode === 'items'
          ? '일괄 아이템 구입'
          : '일괄 포인트 지급';
  const undoLabel =
    displayMode === 'coins'
      ? '마지막 코인 지급 취소'
      : displayMode === 'notes'
        ? '마지막 기록 취소'
        : displayMode === 'items'
          ? '마지막 아이템 취소'
          : '마지막 포인트 지급 취소';

  const openItemGrantForMember = (memberId: string) => {
    const targets =
      selectedMemberIds.size > 0 && selectedMemberIds.has(memberId)
        ? Array.from(selectedMemberIds)
        : selectedMemberIds.size > 0
          ? Array.from(selectedMemberIds)
          : [memberId];
    setItemGrantTargetIds(targets);
    setItemGrantOpen(true);
  };

  const openStoreForMember = (memberId: string) => {
    if (displayMode !== 'items') return;
    setStoreProfileOnly(false);
    setStoreClassOpen(false);
    setStoreMemberId(memberId);
  };

  const openClassStore = () => {
    setStoreProfileOnly(false);
    setStoreMemberId(null);
    setStoreClassOpen(true);
  };

  const openProfileImageSelect = (memberId: string) => {
    setProfileSelectMemberId(memberId);
  };

  const applyProfileImageLocal = (memberId: string, imageId: string) => {
    setAllMembers((prev) => {
      const next = prev.map((m) => (m.id === memberId ? { ...m, profileImage: imageId } : m));
      cacheClassMemberGradeLayout(
        workspaceId,
        currentGradeId,
        buildLayoutFromMembers(groups, next)
      );
      return next;
    });
    setProfileLayoutDirty(true);
  };

  const openProfileStoreFromSelectModal = (memberId: string) => {
    if (!memberId) return;
    setStoreProfileOnly(true);
    setStoreMemberId(memberId);
  };

  const pendingStoreCostForMember = (memberId: string) =>
    (draftStorePurchases[memberId] ?? []).reduce((sum, p) => sum + Math.max(0, p.cost), 0);

  const addStorePurchaseDraft = (memberId: string, card: ItemCard) => {
    const cost = typeof card.cost === 'number' ? card.cost : 0;
    const available =
      memberDisplayCoinTotal(groupPoints, memberId) - pendingStoreCostForMember(memberId);
    if (cost > 0 && available < cost) return;
    setDraftStorePurchases((prev) => ({
      ...prev,
      [memberId]: [
        ...(prev[memberId] ?? []),
        {
          draftId: newId('storeDraft'),
          itemCardId: card.itemCardId,
          cost,
          name: card.name || '(이름 없음)',
          cardImage: card.cardImage,
          type: card.type,
          useCount: card.useCount,
        },
      ],
    }));
  };

  /**
   * 스토어의 '프로필 카드 구입' 탭: 저장 대기 없이 즉시 가중치 랜덤 뽑기 + 코인 차감 + DB 반영.
   * 이미 보유한 카드도 다시 뽑힐 수 있으며(중복), 보유 수량(quantity)이 증가한다.
   */
  const purchaseProfileCardNow = (
    memberId: string,
    storeSet: StoreProfileCardSet
  ): ProfileCardPurchaseResult => {
    if (!selectedGroupId) return { ok: false, reason: 'ALL_UNLOCKED' };
    const set = profileCardSets.find(
      (s) => s.id === storeSet.id && s.source === storeSet.source
    );
    if (!set || !set.enabled) return { ok: false, reason: 'ALL_UNLOCKED' };
    const cost = set.priceCoins;
    const available =
      memberDisplayCoinTotal(groupPoints, memberId) - pendingStoreCostForMember(memberId);
    if (cost > 0 && available < cost) return { ok: false, reason: 'INSUFFICIENT_COINS' };

    const slot = drawRandomProfileCardSlot(set, []);
    if (!slot || !slot.storagePath) return { ok: false, reason: 'ALL_UNLOCKED' };
    const storagePath = slot.storagePath;

    const now = new Date().toISOString();
    const prev = docRef.current;
    const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
    const coinTotals = { ...g.coinTotals };
    let coinRecords = g.coinRecords;
    if (cost > 0) {
      const bal = coinTotals[memberId] ?? 0;
      coinRecords = [
        {
          id: newId('coin'),
          memberId,
          coinValue: -cost,
          coinType: 'Store',
          memo: `${set.name || '프로필 카드'} 세트 구입`,
          recordedDate: selectedDateIso,
          createdAt: now,
        },
        ...coinRecords,
      ];
      coinTotals[memberId] = bal - cost;
    }

    const unlocks = [...(g.profileCardUnlocks ?? [])];
    const sameCard = (u: (typeof unlocks)[number]) =>
      u.memberId === memberId &&
      u.setId === set.id &&
      u.slotIndex === slot.slotIndex &&
      (u.sourceKind ?? 'admin') === set.source;
    // 동일 카드가 로컬에 여러 줄이면 하나로 합친 뒤 수량 +1 (유니크 충돌 예방)
    const matchIdxs = unlocks.reduce<number[]>((acc, u, i) => {
      if (sameCard(u)) acc.push(i);
      return acc;
    }, []);
    if (matchIdxs.length > 0) {
      const primary = matchIdxs[0];
      let qty = 0;
      for (const i of matchIdxs) qty += Math.max(1, unlocks[i].quantity ?? 1);
      unlocks[primary] = {
        ...unlocks[primary],
        sourceKind: set.source,
        quantity: qty + 1,
      };
      for (let j = matchIdxs.length - 1; j >= 1; j--) unlocks.splice(matchIdxs[j], 1);
    } else {
      unlocks.unshift({
        id: newId('profileCard'),
        memberId,
        setId: set.id,
        slotIndex: slot.slotIndex,
        sourceKind: set.source,
        quantity: 1,
        unlockedAt: now,
      });
    }

    const nextDoc: PointItemNoteDoc = {
      ...prev,
      memberPointsByGroup: {
        ...prev.memberPointsByGroup,
        [selectedGroupId]: { ...g, coinRecords, coinTotals, profileCardUnlocks: unlocks },
      },
      updatedAt: now,
    };
    setDoc(nextDoc);
    docRef.current = nextDoc;

    void (async () => {
      try {
        await savePointItemNote(nextDoc, { saveGroups: buildSaveGroups([selectedGroupId]) });
        baselineRef.current = snapshotDoc(nextDoc);
      } catch (e) {
        showError(e instanceof Error ? e.message : '프로필 카드 구매 저장에 실패했습니다.');
      }
    })();

    return {
      ok: true,
      storagePath,
      imageVersion: slot.imageVersion,
      source: set.source,
      slotIndex: slot.slotIndex,
      cardName: profileCardSlotDisplayName(slot),
    };
  };

  /** 중복 보유한 프로필 카드를 등급 환급률로 한꺼번에 교환(1장만 남김) */
  const exchangeProfileCardDuplicates = async (params: {
    memberId: string;
    setId: string;
    sourceKind: ProfileCardSetSource;
    slotIndex: number;
  }): Promise<void> => {
    const { memberId, setId, sourceKind, slotIndex } = params;
    if (!selectedGroupId) return;
    const set = profileCardSets.find((s) => s.id === setId && s.source === sourceKind);
    const slot = set?.slots.find((s) => s.slotIndex === slotIndex);
    const rarity = slot?.rarity ?? null;
    if (!set || !rarity) return;

    const prev = docRef.current;
    const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
    const unlocks = [...(g.profileCardUnlocks ?? [])];
    const idx = unlocks.findIndex(
      (u) =>
        u.memberId === memberId &&
        u.setId === setId &&
        u.slotIndex === slotIndex &&
        (u.sourceKind ?? 'admin') === sourceKind
    );
    if (idx < 0) return;
    const current = unlocks[idx];
    const quantity = current.quantity ?? 1;
    if (quantity < 2) return;

    const deductCount = quantity - 1;
    const coinsPerCard = profileCardExchangeCoinsForRarity(set.priceCoins, rarity);
    const gained = coinsPerCard * deductCount;
    unlocks[idx] = { ...current, quantity: 1 };

    const now = new Date().toISOString();
    const coinTotals = { ...g.coinTotals };
    let coinRecords = g.coinRecords;
    if (gained > 0) {
      const bal = coinTotals[memberId] ?? 0;
      coinRecords = [
        {
          id: newId('coin'),
          memberId,
          coinValue: gained,
          coinType: 'Store',
          memo: `${set.name || '프로필 카드'} 중복 카드 환전 (${deductCount}장)`,
          recordedDate: selectedDateIso,
          createdAt: now,
        },
        ...coinRecords,
      ];
      coinTotals[memberId] = bal + gained;
    }

    const nextDoc: PointItemNoteDoc = {
      ...prev,
      memberPointsByGroup: {
        ...prev.memberPointsByGroup,
        [selectedGroupId]: { ...g, coinRecords, coinTotals, profileCardUnlocks: unlocks },
      },
      updatedAt: now,
    };
    setDoc(nextDoc);
    docRef.current = nextDoc;
    try {
      await savePointItemNote(nextDoc, { saveGroups: buildSaveGroups([selectedGroupId]) });
      baselineRef.current = snapshotDoc(nextDoc);
      showSuccess(`중복 카드 ${deductCount}장을 ${gained}코인으로 교환했습니다.`);
    } catch (e) {
      showError(e instanceof Error ? e.message : '환전 저장에 실패했습니다.');
    }
  };

  /** 세트가 삭제·변경되어 카탈로그에서 찾을 수 없는("알 수 없는 세트") 프로필 카드 보유 이력 삭제 */
  const deleteProfileCardUnlock = async (params: {
    memberId: string;
    setId: string;
    sourceKind: ProfileCardSetSource;
    slotIndex: number;
  }): Promise<void> => {
    const { memberId, setId, sourceKind, slotIndex } = params;
    if (!selectedGroupId) return;

    const prev = docRef.current;
    const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
    const unlocks = (g.profileCardUnlocks ?? []).filter(
      (u) =>
        !(
          u.memberId === memberId &&
          u.setId === setId &&
          u.slotIndex === slotIndex &&
          (u.sourceKind ?? 'admin') === sourceKind
        )
    );

    const nextDoc: PointItemNoteDoc = {
      ...prev,
      memberPointsByGroup: {
        ...prev.memberPointsByGroup,
        [selectedGroupId]: { ...g, profileCardUnlocks: unlocks },
      },
      updatedAt: new Date().toISOString(),
    };
    setDoc(nextDoc);
    docRef.current = nextDoc;
    try {
      await savePointItemNote(nextDoc, { saveGroups: buildSaveGroups([selectedGroupId]) });
      baselineRef.current = snapshotDoc(nextDoc);
      showSuccess('카드를 삭제했습니다.');
    } catch (e) {
      showError(e instanceof Error ? e.message : '카드 삭제 저장에 실패했습니다.');
    }
  };

  /** 일괄 아이템 구입 모달 → 선택 멤버 장바구니(드래프트)에 담기 */
  const addBulkStorePurchaseDraft = (card: ItemCard) => {
    const cost = typeof card.cost === 'number' ? card.cost : 0;
    let added = 0;
    let skipped = 0;
    const appendByMember: Record<
      string,
      {
        draftId: string;
        itemCardId: string;
        cost: number;
        name: string;
        cardImage?: string;
        type: ItemCard['type'];
        useCount?: number;
      }[]
    > = {};

    for (const mid of itemGrantTargetIds) {
      const queued = appendByMember[mid] ?? [];
      const pending =
        (draftStorePurchases[mid] ?? []).reduce((sum, p) => sum + Math.max(0, p.cost), 0) +
        queued.reduce((sum, p) => sum + Math.max(0, p.cost), 0);
      const available = memberDisplayCoinTotal(groupPoints, mid) - pending;
      if (cost > 0 && available < cost) {
        skipped += 1;
        continue;
      }
      appendByMember[mid] = [
        ...queued,
        {
          draftId: newId('storeDraft'),
          itemCardId: card.itemCardId,
          cost,
          name: card.name || '(이름 없음)',
          cardImage: card.cardImage,
          type: card.type,
          useCount: card.useCount,
        },
      ];
      added += 1;
    }

    if (added > 0) {
      setDraftStorePurchases((prev) => {
        const next = { ...prev };
        Object.entries(appendByMember).forEach(([mid, drafts]) => {
          next[mid] = [...(prev[mid] ?? []), ...drafts];
        });
        return next;
      });
    }
    return { added, skipped };
  };

  const removeStorePurchaseDraft = (memberId: string, draftId: string) => {
    setDraftStorePurchases((prev) => {
      const nextList = (prev[memberId] ?? []).filter((p) => p.draftId !== draftId);
      if (nextList.length === 0) {
        const { [memberId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [memberId]: nextList };
    });
  };

  const useInventoryItem = async (instanceId: string, memo: string) => {
    if (!selectedGroupId) throw new Error('그룹을 선택해 주세요.');
    const prev = docRef.current;
    const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
    const list = [...(g.itemInstances ?? [])];
    const idx = list.findIndex((i) => i.id === instanceId);
    if (idx < 0) throw new Error('아이템 카드를 찾을 수 없습니다.');
    const target = list[idx];
    const card = findItemCard(prev.itemCards, target.itemCardId, classPointExtraCards);
    const remaining = resolveRemainingUseCount(target, card);
    if (remaining <= 0) throw new Error('사용 가능한 횟수가 없습니다.');
    const now = new Date().toISOString();
    const usageRecord = memo
      ? { usedAt: now, memo }
      : { usedAt: now };
    list[idx] = {
      ...target,
      remainingUseCount: remaining - 1,
      usageHistory: [...(target.usageHistory ?? []), usageRecord],
    };
    const nextDoc: PointItemNoteDoc = {
      ...prev,
      memberPointsByGroup: {
        ...prev.memberPointsByGroup,
        [selectedGroupId]: { ...g, itemInstances: list },
      },
      updatedAt: now,
    };
    setDoc(nextDoc);
    docRef.current = nextDoc;
    try {
      await savePointItemNote(nextDoc, {
        saveGroups: buildSaveGroups([selectedGroupId]),
      });
      baselineRef.current = snapshotDoc(nextDoc);
    } catch (e) {
      showError(e instanceof Error ? e.message : '사용 저장에 실패했습니다.');
      throw e;
    }
  };

  const deleteInventoryItem = async (instanceId: string) => {
    if (!selectedGroupId) throw new Error('그룹을 선택해 주세요.');
    const prev = docRef.current;
    const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
    const list = (g.itemInstances ?? []).filter((i) => i.id !== instanceId);
    if (list.length === (g.itemInstances ?? []).length) {
      throw new Error('아이템 카드를 찾을 수 없습니다.');
    }
    const now = new Date().toISOString();
    const nextDoc: PointItemNoteDoc = {
      ...prev,
      memberPointsByGroup: {
        ...prev.memberPointsByGroup,
        [selectedGroupId]: { ...g, itemInstances: list },
      },
      updatedAt: now,
    };
    setDoc(nextDoc);
    docRef.current = nextDoc;
    try {
      await savePointItemNote(nextDoc, {
        saveGroups: buildSaveGroups([selectedGroupId]),
      });
      baselineRef.current = snapshotDoc(nextDoc);
      showSuccess('아이템 카드가 삭제되었습니다.');
    } catch (e) {
      showError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
      throw e;
    }
  };

  const confirmItemGrant = async (card: ItemCard) => {
    if (!selectedGroupId || itemGrantTargetIds.length === 0) return;
    setItemGrantOpen(false);
    const now = new Date().toISOString();
    const date = selectedDateIso;
    const acquired: ItemGrantSummaryRow[] = [];
    const missed: ItemGrantSummaryRow[] = [];
    const cost =
      card.category === 'store' && typeof card.cost === 'number' ? card.cost : 0;
    const isPurchase = card.category === 'store' && cost > 0;

    const prev = docRef.current;
    const g = prev.memberPointsByGroup[selectedGroupId] ?? emptyGroupMemberPoints();
    let coinRecords = [...(g.coinRecords ?? [])];
    let coinTotals = { ...(g.coinTotals ?? {}) };
    let itemInstances = [...(g.itemInstances ?? [])];

    for (const mid of itemGrantTargetIds) {
      const member = allMembers.find((x) => x.id === mid);
      const label =
        member != null
          ? `${String(member.orderIndex).padStart(2, '0')}. ${displayClassMemberName(member).trim() || '(이름 없음)'}`
          : mid;

      if (isPurchase) {
        const bal = coinTotals[mid] ?? 0;
        if (bal < cost) {
          missed.push({ memberId: mid, label, reason: `코인 부족 (보유 ${bal})` });
          continue;
        }
        const coinRec: MemberCoinRecord = {
          id: newId('coin'),
          memberId: mid,
          coinValue: -cost,
          coinType: 'Store',
          memo: `${card.name || '아이템'} 구매`,
          recordedDate: date,
          createdAt: now,
        };
        coinRecords = [coinRec, ...coinRecords];
        coinTotals[mid] = bal - cost;
      }

      const inst: MemberItemInstance = {
        id: newId('item'),
        memberId: mid,
        itemCardId: card.itemCardId,
        category: card.category,
        remainingUseCount:
          card.type === 'active' ? Math.max(1, card.useCount ?? 1) : undefined,
        isLocked: false,
        acquiredAt: now,
      };
      itemInstances = [inst, ...itemInstances];
      acquired.push({
        memberId: mid,
        label,
        reason: isPurchase ? `${cost}코인 구매` : undefined,
      });
    }

    const nextDoc: PointItemNoteDoc = {
      ...prev,
      memberPointsByGroup: {
        ...prev.memberPointsByGroup,
        [selectedGroupId]: {
          ...g,
          coinRecords,
          coinTotals,
          itemInstances,
        },
      },
      updatedAt: now,
    };
    setDoc(nextDoc);
    docRef.current = nextDoc;

    setItemSummaryName(card.name || '(아이템)');
    setItemSummaryAcquired(acquired);
    setItemSummaryMissed(missed);
    setItemSummaryOpen(true);

    if (acquired.length === 0) return;
    try {
      showSaving();
      await savePointItemNote(nextDoc, {
        saveGroups: buildSaveGroups([selectedGroupId]),
      });
      baselineRef.current = snapshotDoc(nextDoc);
      showSuccess('아이템이 반영되었습니다.');
    } catch (e) {
      showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    }
  };

  const stepUnit = 1;

  const setDraftValue = (memberId: string, value: number) => {
    setDraftValues((prev) => ({ ...prev, [memberId]: value }));
  };

  const applySetToMember = (memberId: string) => {
    if (displayMode === 'coins') return;
    setPointTypeSelectSource('member');
    setSelectingMemberId(memberId);
    setPointTypeSelectOpen(true);
  };

  const applyPointTypeSelectResult = (result: PointTypeSelectResult) => {
    if (pointTypeSelectSource === 'bulk') {
      if (result.kind === 'manual') {
        setBulkPointTypeId(
          result.category === 'positive' ? 'positive-default' : 'negative-default'
        );
        setBulkPointValue('0');
        setBulkPointMemo('');
      } else {
        setBulkPointTypeId(result.type.id);
        setBulkPointValue(String(result.type.points));
        setBulkPointMemo(result.type.memo);
        setSelectedPointTypeId(result.type.id);
      }
      setPointTypeSelectOpen(false);
      return;
    }

    const targetIds =
      selectingMemberId != null
        ? [selectingMemberId]
        : Array.from(selectedMemberIds);
    if (!targetIds.length) {
      setPointTypeSelectOpen(false);
      setSelectingMemberId(null);
      return;
    }

    if (result.kind === 'manual') {
      const typeId =
        result.category === 'positive' ? 'positive-default' : 'negative-default';
      setDraftPointTypeIds((prev) => {
        const next = { ...prev };
        targetIds.forEach((id) => {
          next[id] = typeId;
        });
        return next;
      });
      setDraftValues((prev) => {
        const next = { ...prev };
        targetIds.forEach((id) => {
          next[id] = 0;
        });
        return next;
      });
      setDraftMemos((prev) => {
        const next = { ...prev };
        targetIds.forEach((id) => {
          next[id] = '';
        });
        return next;
      });
      setSelectedPointTypeId(null);
    } else {
      const { type } = result;
      setSelectedPointTypeId(type.id);
      setDraftPointTypeIds((prev) => {
        const next = { ...prev };
        targetIds.forEach((id) => {
          next[id] = type.id;
        });
        return next;
      });
      setDraftValues((prev) => {
        const next = { ...prev };
        targetIds.forEach((id) => {
          next[id] = type.points;
        });
        return next;
      });
      setDraftMemos((prev) => {
        const next = { ...prev };
        targetIds.forEach((id) => {
          next[id] = type.memo;
        });
        return next;
      });
    }

    setPointTypeSelectOpen(false);
    setSelectingMemberId(null);
  };

  const bulkPointTypeLabel = useMemo(() => {
    if (!bulkPointTypeId) return null;
    if (bulkPointTypeId === 'positive-default') {
      return (
        <>
          <GemStarIcon positive size={16} />
          <span>긍정행동</span>
        </>
      );
    }
    if (bulkPointTypeId === 'negative-default') {
      return (
        <>
          <GemStarIcon positive={false} size={16} />
          <span>행동체크</span>
        </>
      );
    }
    const t = allPointTypes.find((x) => x.id === bulkPointTypeId);
    if (!t) return null;
    return (
      <>
        <PointTypeIcon positive={t.category === 'positive'} emoji={t.emoji} size={16} />
        <span>{t.memo || '세트'}</span>
      </>
    );
  }, [bulkPointTypeId, allPointTypes]);

  const clearMemberDraft = (memberId: string) => {
    setDraftValues((prev) => ({ ...prev, [memberId]: 0 }));
    setDraftMemos((prev) => ({ ...prev, [memberId]: '' }));
    setDraftPointTypeIds((prev) => {
      const next = { ...prev };
      delete next[memberId];
      return next;
    });
  };

  /** 조회 모드(포인트·코인·아이템·기록)에서 마우스를 올린 블럭 하나만 기록 블럭으로 */
  const hoverRecordEnabled = !isRecordMode;
  /** 입력 중인(draft 있는) 학생들: 마우스가 벗어나도 기록 블럭 유지(여러 명 가능) */
  const pendingHoverRecordIds = ((): Set<string> => {
    if (!hoverRecordEnabled) return new Set<string>();
    const ids = new Set<string>();
    Object.entries(draftValues).forEach(([id, v]) => {
      if (typeof v === 'number' && v !== 0) ids.add(id);
    });
    Object.entries(draftMemos).forEach(([id, t]) => {
      if ((t ?? '').trim()) ids.add(id);
    });
    Object.keys(draftPointTypeIds).forEach((id) => ids.add(id));
    Object.entries(draftNoteTypes).forEach(([id, t]) => {
      if (t && t !== '일반') ids.add(id);
    });
    Object.entries(draftStorePurchases).forEach(([id, arr]) => {
      if (arr.length > 0) ids.add(id);
    });
    /* 프로필 클릭으로 선택된 학생도 블럭 유지 */
    selectedMemberIds.forEach((id) => ids.add(id));
    return ids;
  })();
  const isHoverRecordMember = (memberId: string) =>
    pendingHoverRecordIds.has(memberId) ||
    (pointTypeSelectOpen && pointTypeSelectSource === 'member' && selectingMemberId === memberId) ||
    storeMemberId === memberId ||
    focusRecordMemberId === memberId ||
    hoverRecordMemberId === memberId;

  const renderMemberCard = (m: FlatMember) => {
    /** 조회 모드 hover 기록 블럭: 이 카드만 기록 모드 UI로 전환 */
    const hoverRecord = hoverRecordEnabled && isHoverRecordMember(m.id);
    const cardRecord = isRecordMode || hoverRecord;
    const selected = selectedMemberIds.has(m.id);
    // 행동(현재 그룹 records) + 과제(문서 전체·멤버 기준) 합산
    const points = memberDisplayPointTotal(
      doc.memberPointsByGroup,
      groupPoints,
      m.id
    );
    const coins = memberDisplayCoinTotal(groupPoints, m.id);
    const { level, progressPct } = levelFromTotal(
      points,
      doc.levelSettings.levelUpPoints || doc.pointSet.levelUpPoints
    );
    const noteTypeCounts = noteCountsByMember.get(m.id) ?? {
      일반: 0,
      학습: 0,
      행동: 0,
    };
    const notesCount = noteTypeCounts.일반 + noteTypeCounts.학습 + noteTypeCounts.행동;
    const itemCounts = countMemberItems(
      groupPoints.itemInstances,
      m.id,
      doc.itemCards,
      classPointExtraCards
    );
    const usableActiveItems = listUsableActiveItems(
      groupPoints.itemInstances,
      m.id,
      doc.itemCards,
      classPointExtraCards
    );
    const metric = displayMetricStr(displayMode === 'coins' ? coins : points);
    const levelColor = getLevelColor(level);
    const levelTextColor = getLevelTextColor(level);
    const draftVal = draftValues[m.id];
    const draftNum = draftVal === undefined ? 0 : draftVal;
    const draftMemoText = (draftMemos[m.id] ?? '').trim();
    const hasDraftInput =
      displayMode === 'notes'
        ? draftMemoText.length > 0
        : displayMode === 'items'
          ? (draftStorePurchases[m.id] ?? []).length > 0
          : draftNum !== 0 || draftMemoText.length > 0;
    const hasData = cardRecord
      ? hasDraftInput
      : displayMode === 'notes'
        ? notesCount > 0
        : displayMode === 'items'
          ? itemCounts.activeTotal + itemCounts.passiveOwned > 0
          : (displayMode === 'coins' ? coins : points) !== 0;
    const isNotesView = !cardRecord && displayMode === 'notes';
    const isItemsView = !cardRecord && displayMode === 'items';
    const isItemsRecord = cardRecord && displayMode === 'items';
    const draftTypeId = draftPointTypeIds[m.id];
    const memberType = allPointTypes.find((t) => t.id === draftTypeId);
    const setCategory: 'positive' | 'negative' | null =
      draftTypeId === 'positive-default' || memberType?.category === 'positive'
        ? 'positive'
        : draftTypeId === 'negative-default' || memberType?.category === 'negative'
          ? 'negative'
          : null;

    const isSeatCard = layoutMode === 'seat';
    const isNotesRecord = cardRecord && displayMode === 'notes';
    const noteType = draftNoteTypes[m.id] ?? '일반';
    const noteTypeDot =
      noteType === '학습' ? '#4CC7AE' : noteType === '행동' ? '#FF376E' : '#B8B8B8';
    const idBlock = (
      <div
        className={`cp-pin-stu-id${hoverRecord ? ' is-history-link' : ''}`}
        onClick={
          hoverRecord
            ? (e) => {
                e.stopPropagation();
                if (displayMode === 'items') openMemberItemInventory(m.id);
                else openMemberHistory(m.id);
              }
            : undefined
        }
        title={
          hoverRecord
            ? displayMode === 'items'
              ? '보유 아이템 보기'
              : `${historyTitle} 보기`
            : undefined
        }
      >
        <span className="cp-pin-stu-no">{String(m.orderIndex).padStart(2, '0')}</span>
        <span className="cp-pin-stu-name">{displayClassMemberName(m).trim() || '(이름 없음)'}</span>
      </div>
    );
    const avatarBlock = (
      <div
        className="cp-pin-stu-avatar"
        onClick={(e) => {
          if (isRecordMode) {
            toggleMember(m.id);
            return;
          }
          e.stopPropagation();
          /* 개별 기록 모드 hover 블럭: 클릭마다 선택/해제 — 더블클릭은 두 번 토글되어 원래 상태로 돌아오고 모달만 열림 */
          if (hoverRecord) toggleMember(m.id);
        }}
        onMouseDown={(e) => {
          /* 더블클릭 시 브라우저 이미지/텍스트 선택 하이라이트 방지 */
          if (!isRecordMode && e.detail > 1) e.preventDefault();
        }}
        onDoubleClick={(e) => {
          if (isRecordMode) return;
          e.stopPropagation();
          e.preventDefault();
          openProfileImageSelect(m.id);
        }}
        role={isRecordMode ? 'button' : undefined}
        tabIndex={-1}
        title={!isRecordMode ? '더블클릭하여 프로필 이미지 변경' : undefined}
        onKeyDown={(e) => {
          if (isRecordMode && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            toggleMember(m.id);
          }
        }}
      >
        <MemberProfileThumb
          member={m}
          workspaceId={workspaceId}
          gradeId={currentGradeId}
          className="cp-pin-stu-avatar-img"
          fallback={
            <span className="cp-pin-stu-avatar-fallback">{(displayClassMemberName(m).trim() || '?').slice(0, 1)}</span>
          }
        />
        {(isRecordMode || hoverRecord) && selected ? (
          <span className="cp-pin-stu-check" aria-hidden>
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong, #10504a)" />
              <path
                d="M10 16L14 20L22 12"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ) : null}
      </div>
    );

    const cycleNoteType = () => {
      const cur = draftNoteTypes[m.id] ?? '일반';
      const idx = NOTE_TYPES.indexOf(cur);
      setDraftNoteTypes((prev) => ({
        ...prev,
        [m.id]: NOTE_TYPES[(idx + 1) % NOTE_TYPES.length],
      }));
    };

    return (
      <div
        key={m.id}
        className={`cp-pin-stu-card${selected ? ' is-selected' : ''}${
          cardRecord ? ' is-record' : ' is-view'
        }${hasData ? ' has-data' : ''} cp-pin-stu-card--${displayMode}${
          isSeatCard && !isNotesRecord ? ' is-seat-card' : ''
        }${!cardRecord ? ' is-clickable' : ''}${hoverRecordEnabled ? ' is-hover-swap' : ''}`}
        data-tutorial-pin="stu-card"
        data-pin-member-id={m.id}
        onMouseEnter={
          hoverRecordEnabled
            ? () => {
                if (hoverLeaveTimerRef.current != null) {
                  window.clearTimeout(hoverLeaveTimerRef.current);
                  hoverLeaveTimerRef.current = null;
                }
                setHoverRecordMemberId(m.id);
              }
            : undefined
        }
        onMouseLeave={
          hoverRecordEnabled
            ? () => {
                if (hoverLeaveTimerRef.current != null) window.clearTimeout(hoverLeaveTimerRef.current);
                hoverLeaveTimerRef.current = window.setTimeout(() => {
                  hoverLeaveTimerRef.current = null;
                  setHoverRecordMemberId((cur) => (cur === m.id ? null : cur));
                }, 80);
              }
            : undefined
        }
        onFocusCapture={
          hoverRecordEnabled
            ? (e) => {
                /* 글자 입력칸(값·메모·노트)에 포커스가 있을 때만 블럭 유지 — 버튼 클릭 포커스는 제외 */
                const t = e.target as HTMLElement;
                if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') {
                  setFocusRecordMemberId(m.id);
                } else {
                  setFocusRecordMemberId((cur) => (cur === m.id ? null : cur));
                }
              }
            : undefined
        }
        onBlurCapture={
          hoverRecordEnabled
            ? (e) => {
                const next = e.relatedTarget as HTMLElement | null;
                if (
                  next &&
                  e.currentTarget.contains(next) &&
                  (next.tagName === 'INPUT' || next.tagName === 'TEXTAREA')
                ) {
                  return;
                }
                setFocusRecordMemberId((cur) => (cur === m.id ? null : cur));
              }
            : undefined
        }
        role={!cardRecord ? 'button' : undefined}
        tabIndex={!cardRecord ? 0 : undefined}
        onClick={() => {
          if (cardRecord) return;
          if (displayMode === 'items') {
            openMemberItemInventory(m.id);
            return;
          }
          openMemberHistory(m.id);
        }}
        onKeyDown={(e) => {
          if (cardRecord) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (displayMode === 'items') {
              openMemberItemInventory(m.id);
              return;
            }
            openMemberHistory(m.id);
          }
        }}
      >
        <div
          className={`cp-pin-stu-layout${
            isNotesView ? ' is-notes' : ''
          }${isItemsView || isItemsRecord ? ' is-items' : ''}${
            cardRecord && displayMode !== 'notes' && displayMode !== 'items'
              ? ' is-record-layout'
              : ''
          }${isNotesRecord ? ' is-notes-record' : ''}${
            isItemsRecord ? ' is-items-record' : ''
          }${isSeatCard && !isNotesRecord && !isItemsRecord ? ' is-seat-layout' : ''}`}
        >
          {isNotesRecord ? (
            <>
              <div className="cp-pin-stu-note-left">
                {avatarBlock}
                <button
                  type="button"
                  className="cp-pin-note-type-btn"
                  data-tutorial-pin="note-type"
                  onClick={(e) => {
                    e.stopPropagation();
                    cycleNoteType();
                  }}
                >
                  <span
                    className="cp-pin-note-type-dot"
                    style={{ background: noteTypeDot }}
                    aria-hidden
                  />
                  {noteType}
                </button>
              </div>
              <div className="cp-pin-stu-note-right">
                <div className="cp-pin-stu-note-name-row">
                  <span className="cp-pin-stu-person-icon" aria-hidden>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </span>
                  <span
                    className={`cp-pin-stu-note-name${hoverRecord ? ' is-history-link' : ''}`}
                    onClick={
                      hoverRecord
                        ? (e) => {
                            e.stopPropagation();
                            openMemberHistory(m.id);
                          }
                        : undefined
                    }
                    title={hoverRecord ? `${historyTitle} 보기` : undefined}
                  >
                    <span className="cp-pin-stu-no">
                      {String(m.orderIndex).padStart(2, '0')}
                    </span>
                    {displayClassMemberName(m).trim() || '(이름 없음)'}
                  </span>
                </div>
                <textarea
                  className="cp-pin-stu-note-textarea"
                  rows={2}
                  placeholder="노트를 입력하세요"
                  value={draftMemos[m.id] ?? ''}
                  onChange={(e) => {
                    setDraftMemos((prev) => ({ ...prev, [m.id]: e.target.value }));
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </>
          ) : isItemsRecord ? (
            <>
              <div className="cp-pin-stu-store-left">
                {avatarBlock}
                <button
                  type="button"
                  className="cp-pin-stu-store-btn"
                  data-tutorial-pin="store-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openStoreForMember(m.id);
                  }}
                  title="스토어"
                  aria-label="스토어 열기"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.8}
                    stroke="currentColor"
                    width="16"
                    height="16"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"
                    />
                  </svg>
                </button>
              </div>
              <div className="cp-pin-stu-store-right">
                <div className="cp-pin-stu-top">
                  {idBlock}
                  <div className="cp-pin-stu-cart-meta">
                    <span
                      className="cp-pin-stu-cart-cost"
                      title="사용 예상 금액"
                      aria-label={`사용 예상 금액 ${pendingStoreCostForMember(m.id)}`}
                    >
                      <span className="cp-pin-stu-coin-icon" aria-hidden>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#f39c12" />
                          <circle cx="12" cy="12" r="8" fill="#f1c40f" />
                          <text
                            x="12"
                            y="16"
                            fontSize="12"
                            fontWeight="bold"
                            fill="#e67e22"
                            textAnchor="middle"
                          >
                            ₩
                          </text>
                        </svg>
                      </span>
                      <span className="cp-pin-stu-cart-cost-num">
                        {pendingStoreCostForMember(m.id)}
                      </span>
                    </span>
                    <span className="cp-pin-stu-cart-title">장바구니</span>
                  </div>
                </div>
                <div className="cp-pin-stu-cart">
                  <div className="cp-pin-stu-store-row">
                    {(draftStorePurchases[m.id] ?? []).map((p) => {
                      const catalog = findItemCard(doc.itemCards, p.itemCardId);
                      const imageSrc =
                        resolveItemCardImageSrc(catalog?.cardImage) ??
                        resolveItemCardImageSrc(p.cardImage);
                      return (
                        <div
                          key={p.draftId}
                          className="cp-pin-stu-store-draft-card"
                          title={catalog?.name || p.name}
                        >
                          <span className="cp-pin-stu-store-draft-thumb" aria-hidden>
                            {imageSrc ? (
                              <img src={imageSrc} alt="" />
                            ) : (
                              <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                aria-hidden
                              >
                                <rect
                                  x="2"
                                  y="6"
                                  width="12"
                                  height="16"
                                  rx="2"
                                  fill="#f0f4f3"
                                  stroke="#555"
                                  strokeWidth="1.5"
                                  transform="rotate(-10 8 14)"
                                />
                                <rect
                                  x="6"
                                  y="4"
                                  width="12"
                                  height="16"
                                  rx="2"
                                  fill="#f8f8f8"
                                  stroke="#555"
                                  strokeWidth="1.5"
                                />
                                <rect
                                  x="10"
                                  y="6"
                                  width="12"
                                  height="16"
                                  rx="2"
                                  fill="#fafafa"
                                  stroke="#555"
                                  strokeWidth="1.5"
                                  transform="rotate(10 16 14)"
                                />
                              </svg>
                            )}
                          </span>
                          <span className="cp-pin-stu-store-draft-name">
                            {catalog?.name || p.name}
                          </span>
                          <button
                            type="button"
                            className="cp-pin-stu-store-draft-remove"
                            aria-label={`${catalog?.name || p.name} 구매 취소`}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeStorePurchaseDraft(m.id, p.draftId);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
          {avatarBlock}

          <div className="cp-pin-stu-info">
            <div className="cp-pin-stu-top">
              {idBlock}
              {/* 번호모드: 레벨 배지는 이름 옆. 자리모드는 오른쪽 게이지 위에 배치. 아이템 모드에서는 생략 */}
              {!cardRecord && !isSeatCard && displayMode !== 'items' ? (
                <span
                  className="cp-pin-stu-lv-badge"
                  style={{ background: levelColor, color: levelTextColor }}
                >
                  {level}
                </span>
              ) : null}
            </div>

            {isNotesView ? (
              <>
                <div className="cp-pin-stu-note-middle-name">
                  <span className="cp-pin-stu-note-middle-name-text">
                    {displayClassMemberName(m).trim() || '(이름 없음)'}
                  </span>
                </div>
                <div className="cp-pin-stu-note-count-row">
                  <span
                    className="cp-pin-stu-note-type-badge"
                    style={{ background: '#B8B8B8' }}
                    title="일반"
                  >
                    {noteTypeCounts.일반}
                  </span>
                  <span
                    className="cp-pin-stu-note-type-badge"
                    style={{ background: '#4CC7AE' }}
                    title="학습"
                  >
                    {noteTypeCounts.학습}
                  </span>
                  <span
                    className="cp-pin-stu-note-type-badge"
                    style={{ background: '#FF376E' }}
                    title="행동"
                  >
                    {noteTypeCounts.행동}
                  </span>
                </div>
              </>
            ) : isItemsView ? (
              <div className="cp-pin-stu-store-row cp-pin-stu-item-view-row">
                {usableActiveItems.length === 0 ? (
                  <span
                    className="cp-pin-stu-store-draft-card cp-pin-stu-item-view-empty-slot"
                    aria-label="사용 가능한 카드 없음"
                    title="사용 가능한 카드 없음"
                  />
                ) : (
                  usableActiveItems.map(({ inst, card }) => {
                    const imageSrc = resolveItemCardImageSrc(card?.cardImage);
                    const name = card?.name?.trim() || '(이름 없음)';
                    return (
                      <div
                        key={inst.id}
                        className="cp-pin-stu-store-draft-card"
                        title={`${name} · 남은 횟수 ${resolveRemainingUseCount(inst, card)}`}
                      >
                        <span className="cp-pin-stu-store-draft-thumb" aria-hidden>
                          {imageSrc ? (
                            <img src={imageSrc} alt="" />
                          ) : (
                            <svg
                              width="28"
                              height="28"
                              viewBox="0 0 24 24"
                              fill="none"
                              aria-hidden
                            >
                              <rect
                                x="2"
                                y="6"
                                width="12"
                                height="16"
                                rx="2"
                                fill="#f0f4f3"
                                stroke="#555"
                                strokeWidth="1.5"
                                transform="rotate(-10 8 14)"
                              />
                              <rect
                                x="6"
                                y="4"
                                width="12"
                                height="16"
                                rx="2"
                                fill="#f8f8f8"
                                stroke="#555"
                                strokeWidth="1.5"
                              />
                              <rect
                                x="10"
                                y="6"
                                width="12"
                                height="16"
                                rx="2"
                                fill="#fafafa"
                                stroke="#555"
                                strokeWidth="1.5"
                                transform="rotate(10 16 14)"
                              />
                            </svg>
                          )}
                        </span>
                        <span className="cp-pin-stu-store-draft-name">{name}</span>
                        <span className="cp-pin-stu-item-view-uses">
                          ×{resolveRemainingUseCount(inst, card)}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            ) : !cardRecord ? (
              <>
                <div className={`cp-pin-stu-icon-row${displayMode === 'coins' ? ' is-coin' : ''}`}>
                  {displayMode === 'coins' ? (
                    <span className="cp-pin-stu-coin-icon" aria-hidden>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="#f39c12" />
                        <circle cx="12" cy="12" r="8" fill="#f1c40f" />
                        <text
                          x="12"
                          y="16"
                          fontSize="12"
                          fontWeight="bold"
                          fill="#e67e22"
                          textAnchor="middle"
                        >
                          ₩
                        </text>
                      </svg>
                    </span>
                  ) : (
                    <span className="cp-pin-stu-point-icon" aria-hidden>
                      <img src={pointIconUrl} alt="" width={18} height={18} />
                    </span>
                  )}
                  <span
                    className="cp-pin-stu-compact-x"
                    style={{ visibility: metric.compact ? 'visible' : 'hidden' }}
                  >
                    ×
                  </span>
                  <span
                    className="cp-pin-stu-compact"
                    style={{ visibility: metric.compact ? 'visible' : 'hidden' }}
                  >
                    {metric.compact || '0'}
                  </span>
                </div>
                <div
                  className={`cp-pin-stu-total${displayMode === 'coins' ? ' is-coin' : ''}`}
                  data-digits={metric.digitCount}
                >
                  {metric.text}
                </div>
              </>
            ) : (
              <div className="cp-pin-stu-point-input-wrap">
                <input
                  className={`cp-pin-stu-value-input${displayMode === 'coins' ? ' is-coin' : ''}${
                    draftNum === 0 ? ' is-zero' : ''
                  }${displayMode === 'coins' && draftNum < 0 ? ' is-neg' : ''}`}
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={draftVal === undefined || draftVal === 0 ? '' : String(draftVal)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (displayMode === 'coins') {
                      if (raw === '' || raw === '-') {
                        setDraftValue(m.id, 0);
                        return;
                      }
                      if (/^-?\d+$/.test(raw)) setDraftValue(m.id, parseInt(raw, 10));
                    } else if (raw === '' || /^\d+$/.test(raw)) {
                      setDraftValue(m.id, raw === '' ? 0 : parseInt(raw, 10));
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>

          {cardRecord && displayMode !== 'notes' && displayMode !== 'items' ? (
            <>
              <div className="cp-pin-stu-bottom" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="cp-pin-stu-set-btn"
                  tabIndex={-1}
                  disabled={displayMode === 'coins'}
                  onClick={() => applySetToMember(m.id)}
                  title="포인트 타입 및 세트 선택"
                >
                  {setCategory ? (
                    <PointTypeIcon
                      positive={setCategory === 'positive'}
                      emoji={memberType?.emoji}
                      size={16}
                    />
                  ) : (
                    '세트'
                  )}
                </button>
                <input
                  className="cp-pin-stu-memo-input"
                  type="text"
                  placeholder="메모를 입력하세요"
                  value={draftMemos[m.id] ?? ''}
                  onChange={(e) => {
                    setDraftMemos((prev) => ({ ...prev, [m.id]: e.target.value }));
                  }}
                />
              </div>
              <div className="cp-pin-stu-right is-controls" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="cp-pin-stu-ctrl-btn"
                  tabIndex={-1}
                  aria-label="증가"
                  onClick={() => {
                    setDraftValue(m.id, draftNum + stepUnit);
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                    <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="cp-pin-stu-ctrl-btn"
                  tabIndex={-1}
                  aria-label="초기화"
                  onClick={() => clearMemberDraft(m.id)}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                    <rect x="1" y="1" width="8" height="8" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="cp-pin-stu-ctrl-btn"
                  tabIndex={-1}
                  aria-label="감소"
                  onClick={() => {
                    const next =
                      displayMode === 'coins'
                        ? draftNum - stepUnit
                        : Math.max(0, draftNum - stepUnit);
                    setDraftValue(m.id, next);
                  }}
                >
                  <svg width="12" height="2" viewBox="0 0 12 2" fill="none" aria-hidden>
                    <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </>
          ) : null}

          {!cardRecord && displayMode !== 'items' ? (
            <div className={`cp-pin-stu-right${isSeatCard ? ' is-seat-level' : ''}`}>
              {isSeatCard ? (
                <span
                  className="cp-pin-stu-lv-badge"
                  style={{ background: levelColor, color: levelTextColor }}
                >
                  {level}
                </span>
              ) : null}
              <div
                className="cp-pin-stu-level-bar"
                aria-hidden
                style={
                  {
                    '--cp-pin-level-fill': levelColor,
                    '--cp-pin-level-pct': `${progressPct}%`,
                  } as React.CSSProperties
                }
              />
            </div>
          ) : null}
            </>
          )}
        </div>
      </div>
    );
  };

  if (!gradeReady) {
    return (
      <div className={isPresentation ? 'cp-apn-card-panel cp-pin-pres-panel' : 'cp-apn-page cp-pin-page'}>
        {!isPresentation ? (
          <header className="cp-apn-page-header">
            <h1>포인트 코인 아이템</h1>
          </header>
        ) : null}
        <div className="cp-apn-integrated-hint">
          <p>포인트 코인 아이템은 클래스별로 관리됩니다.</p>
          <p>상단에서 클래스를 선택해 주세요. (통합 보기에서는 사용할 수 없습니다.)</p>
        </div>
      </div>
    );
  }

  const supportsListLayout = displayMode === 'items' || displayMode === 'notes';
  const effectiveLayout: LayoutMode =
    !supportsListLayout && layoutMode === 'list' ? 'number' : layoutMode;

  const membersBoard =
    effectiveLayout === 'seat' && seatViewData.positioned.length > 0 ? (
      <div className="cp-pin-seat-area">
        <div className="cp-pin-seat-scroll">
          <div
            className="cp-pin-seat-grid"
            style={{
              gridTemplateColumns: seatViewData.colTemplate,
              gridTemplateRows: seatViewData.rowTemplate,
            }}
          >
            {seatViewData.positioned.map(({ member, gridRow, gridCol }) => (
              <div
                key={member.id}
                className="cp-pin-seat-cell"
                style={{
                  gridRow: `${gridRow} / span 2`,
                  gridColumn: `${gridCol} / span 2`,
                }}
              >
                {renderMemberCard(member)}
              </div>
            ))}
          </div>
        </div>
        <div className="cp-pin-seat-rotate-wrap">
          <button
            type="button"
            className="cp-pin-seat-rotate-btn"
            title="180도 회전"
            aria-label="자리 배치 180도 회전"
            onClick={() => setIsSeatRotated((v) => !v)}
          >
            <span className="cp-pin-seat-rotate-arrows" aria-hidden>
              <SeatRotateIcon size={14} />
            </span>
            <span>180</span>
          </button>
        </div>
        {seatViewData.unseated.length > 0 ? (
          <>
            <p className="cp-apn-seat-unassigned-title">자리 미지정 멤버</p>
            <div className="cp-pin-stu-grid">{seatViewData.unseated.map(renderMemberCard)}</div>
          </>
        ) : null}
      </div>
    ) : (
      <div className={`cp-pin-stu-grid${effectiveLayout === 'list' ? ' is-list' : ''}`}>
        {members.map(renderMemberCard)}
      </div>
    );

  const layoutToggle = (
    <div
      className={
        `cp-apn-card-view-toggle` +
        (supportsListLayout ? ' is-three' : '') +
        (effectiveLayout === 'seat' ? ' is-seat' : '') +
        (effectiveLayout === 'list' ? ' is-list' : '')
      }
      role="group"
      aria-label={supportsListLayout ? '번호 자리 리스트 보기 전환' : '번호 자리 보기 전환'}
      data-tutorial-pin="layout"
    >
      <button
        type="button"
        className={effectiveLayout === 'number' ? 'is-active' : ''}
        onClick={() => applyLayoutMode('number')}
      >
        번호
      </button>
      <button
        type="button"
        className={effectiveLayout === 'seat' ? 'is-active' : ''}
        onClick={() => applyLayoutMode('seat')}
      >
        자리
      </button>
      {supportsListLayout ? (
        <button
          type="button"
          className={effectiveLayout === 'list' ? 'is-active' : ''}
          onClick={() => applyLayoutMode('list')}
        >
          리스트
        </button>
      ) : null}
    </div>
  );

  const topToolbar = (
    <div className="cp-pin-top-row">
      <div className="cp-pin-top-left">
        <div
          className="cp-pin-date-chip cp-pin-date-chip--clickable"
          title="달력에서 날짜 선택"
          data-tutorial-pin="date"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('.cp-pin-date-part, .cp-pin-date-input')) return;
            setDateCalendarOpen(true);
          }}
        >
          <button
            type="button"
            className="cp-pin-date-cal-btn"
            title="달력에서 날짜 선택"
            aria-label="달력에서 날짜 선택"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect
                x="3"
                y="5"
                width="18"
                height="16"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </button>
          <div className="cp-pin-date-text">
            <div className="cp-pin-date-year-row">
              {dateEditingField === 'year' ? (
                <input
                  className="cp-pin-date-input"
                  value={dateTempValue}
                  onChange={(e) => setDateTempValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitDateEdit('year');
                    if (e.key === 'Escape') {
                      setDateEditingField(null);
                      setDateTempValue('');
                    }
                  }}
                  onBlur={() => commitDateEdit('year')}
                  autoFocus
                  aria-label="연도"
                />
              ) : (
                <button type="button" className="cp-pin-date-part" onClick={() => beginDateEdit('year')}>
                  {dateYear}년
                </button>
              )}
            </div>
            <div className="cp-pin-date-md-row">
              {dateEditingField === 'month' ? (
                <input
                  className="cp-pin-date-input"
                  value={dateTempValue}
                  onChange={(e) => setDateTempValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitDateEdit('month');
                    if (e.key === 'Escape') {
                      setDateEditingField(null);
                      setDateTempValue('');
                    }
                  }}
                  onBlur={() => commitDateEdit('month')}
                  autoFocus
                  aria-label="월"
                />
              ) : (
                <button type="button" className="cp-pin-date-part" onClick={() => beginDateEdit('month')}>
                  {dateMonth}월
                </button>
              )}
              {dateEditingField === 'day' ? (
                <input
                  className="cp-pin-date-input"
                  value={dateTempValue}
                  onChange={(e) => setDateTempValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitDateEdit('day');
                    if (e.key === 'Escape') {
                      setDateEditingField(null);
                      setDateTempValue('');
                    }
                  }}
                  onBlur={() => commitDateEdit('day')}
                  autoFocus
                  aria-label="일"
                />
              ) : (
                <button type="button" className="cp-pin-date-part" onClick={() => beginDateEdit('day')}>
                  {dateDay}일
                </button>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="cp-pin-timetag-chip"
          data-tutorial-pin="timetag"
          onClick={() => setTimeTagPickerOpen(true)}
          title="타임 태그 선택·수정"
          aria-label="타임 태그 선택"
        >
          <span className="cp-pin-timetag-day">{dayBadge}</span>
          <span className="cp-pin-timetag-info">
            <strong>{selectedTimeTag?.name ?? '타임 태그'}</strong>
            <small>
              {selectedTimeTag
                ? `${selectedTimeTag.startTime} ~ ${selectedTimeTag.endTime}`
                : '선택 안됨'}
            </small>
          </span>
        </button>
      </div>

      <button
        ref={modePillAnchorRef}
        type="button"
        className={`cp-pin-mode-pill${isRecordMode ? ' is-record' : ' is-view'}${
          modePillFloating ? ' is-float-anchor-hidden' : ''
        }`}
        data-tutorial-pin="mode"
        aria-pressed={isRecordMode}
        aria-label={
          isRecordMode
            ? '전체 기록 모드, 클릭하면 개별 기록 모드로 전환'
            : '개별 기록 모드, 클릭하면 전체 기록 모드로 전환'
        }
        onClick={toggleInteractionMode}
      >
        <span className="cp-pin-mode-label">
          {isRecordMode ? '전체 기록 모드' : '개별 기록 모드'}
        </span>
        <span className="cp-pin-mode-knob" aria-hidden>
          {isRecordMode ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25z"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M20.71 7.04c.39-.39.39-1.02 0-1.41L18.37 3.29a1 1 0 00-1.41 0l-.88.88 3.75 3.75.88-.88z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2" />
              <path
                d="M20 20L16.65 16.65"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </span>
      </button>

      <button
        type="button"
        className={`cp-pin-save-chip${modePillFloating ? ' is-float-anchor-hidden' : ''}`}
        data-tutorial-pin="save"
        aria-label={displayMode === 'items' ? '구매하기' : '저장하기'}
        disabled={saving || !isDirty || !groupDataReady}
        onClick={() => void handleSave()}
      >
        {saving
          ? displayMode === 'items'
            ? '구매 중…'
            : '저장 중…'
          : displayMode === 'items'
            ? '구매하기'
            : '저장하기'}
      </button>
    </div>
  );

  return (
    <div
      className={
        (isPresentation ? 'cp-apn-card-panel cp-pin-pres-panel' : 'cp-apn-page cp-pin-page') +
        (effectiveLayout === 'list' ? ' is-list-layout' : '')
      }
    >
      {isPresentation ? (
        <div className="cp-apn-card-top">
          <NoteGroupSelector
            groups={groups}
            selectedGroupId={selectedGroupId}
            onChange={(id) => {
              setSelectedGroupId(id);
              saveNoteSelectedGroupId('pointItem', workspaceId, currentGradeId, id);
            }}
          />
          <div className="cp-apn-card-top-actions">
            <div className="cp-apn-header-icon-btn-wrap cp-pin-page-store-btn-wrap">
              <button
                type="button"
                className="cp-pin-page-store-btn"
                data-tutorial-pin="class-store-btn-pres"
                onClick={openClassStore}
                aria-label="스토어 열기"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.8}
                  stroke="currentColor"
                  width="20"
                  height="20"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"
                  />
                </svg>
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                스토어
              </span>
            </div>
          </div>
        </div>
      ) : (
        <header className="cp-apn-page-header">
          <div className="cp-apn-page-title-row">
            <h1>포인트 코인 아이템</h1>
            <NoteGroupSelector
              groups={groups}
              selectedGroupId={selectedGroupId}
              onChange={(id) => {
                setSelectedGroupId(id);
                saveNoteSelectedGroupId('pointItem', workspaceId, currentGradeId, id);
              }}
            />
          </div>
          <div className="cp-apn-page-header-actions">
            <div className="cp-apn-header-icon-btn-wrap">
              <button
                type="button"
                className="cp-apn-header-icon-btn"
                onClick={() => setBulkProfileOpen(true)}
                disabled={members.length === 0}
                aria-label="프로필 이미지 설정"
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="12" cy="12" r="9.2" stroke="currentColor" strokeWidth="1.6" />
                  <circle cx="12" cy="9.6" r="3.2" stroke="currentColor" strokeWidth="1.6" />
                  <path
                    d="M5.9 18.6c1.3-2.5 3.5-3.8 6.1-3.8s4.8 1.3 6.1 3.8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                프로필 이미지 설정
              </span>
            </div>
            <div className="cp-apn-header-icon-btn-wrap">
              <button
                type="button"
                className="cp-apn-header-icon-btn"
                data-tutorial-pin="open-point-set"
                onClick={() => setPointSetOpen(true)}
                aria-label="포인트 세트 설정"
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                    d="M12 2.6l2.72 5.51 6.08.88-4.4 4.29 1.04 6.06L12 16.48l-5.44 2.86 1.04-6.06-4.4-4.29 6.08-.88L12 2.6z"
                  />
                </svg>
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                포인트 세트 설정
              </span>
            </div>
            <div className="cp-apn-header-icon-btn-wrap">
              <button
                type="button"
                className="cp-apn-header-icon-btn"
                data-tutorial-pin="open-item-card"
                onClick={() => setItemCardOpen(true)}
                aria-label="아이템 카드 설정"
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect
                    x="2"
                    y="6"
                    width="12"
                    height="16"
                    rx="2"
                    fill="var(--color-surface, #fff)"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    transform="rotate(-10 8 14)"
                  />
                  <rect
                    x="6"
                    y="4"
                    width="12"
                    height="16"
                    rx="2"
                    fill="var(--color-surface, #fff)"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="10"
                    y="6"
                    width="12"
                    height="16"
                    rx="2"
                    fill="var(--color-surface, #fff)"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    transform="rotate(10 16 14)"
                  />
                </svg>
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                아이템 카드 설정
              </span>
            </div>
            <div className="cp-apn-header-icon-btn-wrap">
              <button
                type="button"
                className="cp-apn-header-icon-btn"
                data-tutorial-pin="open-level"
                onClick={() => setLevelSettingsOpen(true)}
                aria-label="레벨 설정"
              >
                <span className="cp-apn-header-lv-label" aria-hidden>
                  LV
                </span>
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                레벨 설정
              </span>
            </div>
            <div className="cp-apn-header-icon-btn-wrap">
              <button
                type="button"
                className="cp-apn-header-icon-btn"
                data-tutorial-pin="open-class-point"
                onClick={() => setClassPointOpen(true)}
                aria-label="학급 포인트 설정"
              >
                <img src="/StarPoints.svg" alt="" aria-hidden />
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                학급 포인트 설정
              </span>
            </div>
            <div className="cp-apn-header-divider"></div>
            <div className="cp-apn-header-icon-btn-wrap cp-pin-page-store-btn-wrap">
              <button
                type="button"
                className="cp-pin-page-store-btn"
                data-tutorial-pin="class-store-btn"
                onClick={openClassStore}
                aria-label="스토어 열기"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.8}
                  stroke="currentColor"
                  width="20"
                  height="20"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"
                  />
                </svg>
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                스토어
              </span>
            </div>
          </div>
        </header>
      )}

      {loading ? (
        <div className="cp-apn-integrated-hint">
          <p>불러오는 중…</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="cp-apn-integrated-hint">
          <p>클래스 멤버에서 그룹을 먼저 만들어 주세요.</p>
        </div>
      ) : isPresentation ? (
        <>
          <div className="cp-apn-card-tabs cp-apn-card-tabs--3" role="tablist" aria-label="포인트 유형">
            {(
              [
                ['points', '포인트'],
                ['coins', '코인'],
                ['items', '아이템'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={displayMode === key}
                className={`cp-apn-card-tab${displayMode === key ? ' is-active' : ''}`}
                onClick={() => {
                  setDisplayModeAndPersist(key);
                  setSelectedMemberIds(new Set());
                }}
              >
                <span className={`cp-apn-card-tab-icon cp-apn-card-tab-icon--${key}`} aria-hidden>
                  <PointItemCategoryTabIcon mode={key} size={18} />
                </span>
                <span className="cp-apn-card-tab-label">{label}</span>
              </button>
            ))}
          </div>

          <div className="cp-apn-card-chips-scroll cp-pin-pres-top-wrap">
            {topToolbar}
          </div>

          <div className="cp-apn-card-body">
            <div className="cp-apn-card-controls">
              {layoutToggle}
              <div className="cp-pin-action-pills">
                <button type="button" onClick={selectAll}>
                  전체선택
                </button>
                <button
                  type="button"
                  disabled={!isRecordMode && selectedMemberIds.size === 0}
                  onClick={clearSelection}
                >
                  선택취소
                </button>
                <button
                  type="button"
                  disabled={!bulkEnabled}
                  onClick={openBulkModal}
                >
                  {bulkLabel}
                </button>
                <button type="button" disabled={!isDirty} onClick={handleReset}>
                  초기화
                </button>
                <button
                  type="button"
                  disabled={!isRecordMode || displayMode === 'items' || saving || undoing}
                  onClick={undoLast}
                >
                  {undoLabel}
                </button>
              </div>
            </div>
            <div className="cp-apn-card-members">
              {(doc.classPointSettings ?? emptyClassPointSettings()).enabled ? (
                <ClassPointGaugeBar
                  workspaceId={workspaceId}
                  gradeId={currentGradeId}
                  classTotal={classIntegratedPointTotal}
                  tiers={doc.classPointSettings?.rewardTiers ?? []}
                  claimableTierIds={classPointClaimableTiers.map((t) => t.id)}
                  onClaimClick={() => void handleClassPointClaim()}
                />
              ) : null}
              {members.length === 0 ? (
                <div className="cp-apn-card-empty">멤버 데이터가 없습니다.</div>
              ) : (
                membersBoard
              )}
            </div>
          </div>

        </>
      ) : (
        <>
          <div className="cp-apn-card-tabs" role="tablist" aria-label="포인트 유형">
            {(
              [
                ['points', '포인트'],
                ['coins', '코인'],
                ['items', '아이템'],
                ['notes', '기록'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={displayMode === key}
                className={`cp-apn-card-tab${displayMode === key ? ' is-active' : ''}`}
                data-tutorial-pin={key === 'points' ? 'data-mode' : undefined}
                onClick={() => {
                  setDisplayModeAndPersist(key);
                  setSelectedMemberIds(new Set());
                }}
              >
                <span className={`cp-apn-card-tab-icon cp-apn-card-tab-icon--${key}`} aria-hidden>
                  <PointItemCategoryTabIcon mode={key} size={18} />
                </span>
                <span className="cp-apn-card-tab-label">{label}</span>
              </button>
            ))}
          </div>

          <div className="cp-apn-card-chips-scroll cp-pin-pres-top-wrap">
            {topToolbar}
          </div>

          <div className="cp-pin-page-body">
            <div className="cp-apn-card-controls">
              {layoutToggle}
              <div className="cp-pin-action-pills" data-tutorial-pin="actions">
                <button type="button" onClick={selectAll}>
                  전체선택
                </button>
                <button
                  type="button"
                  disabled={!isRecordMode && selectedMemberIds.size === 0}
                  onClick={clearSelection}
                >
                  선택취소
                </button>
                <button
                  type="button"
                  data-tutorial-pin="bulk"
                  disabled={!bulkEnabled}
                  onClick={openBulkModal}
                >
                  {bulkLabel}
                </button>
                <button type="button" disabled={!isDirty} onClick={handleReset}>
                  초기화
                </button>
                <button
                  type="button"
                  disabled={!isRecordMode || displayMode === 'items' || saving || undoing}
                  onClick={undoLast}
                >
                  {undoLabel}
                </button>
              </div>
            </div>

            <div className="cp-pin-page-members">
              {(doc.classPointSettings ?? emptyClassPointSettings()).enabled ? (
                <ClassPointGaugeBar
                  workspaceId={workspaceId}
                  gradeId={currentGradeId}
                  classTotal={classIntegratedPointTotal}
                  tiers={doc.classPointSettings?.rewardTiers ?? []}
                  claimableTierIds={classPointClaimableTiers.map((t) => t.id)}
                  onClaimClick={() => void handleClassPointClaim()}
                />
              ) : null}
              {membersBoard}
            </div>
          </div>
        </>
      )}

      <BulkGrantModal
        open={bulkModalOpen && displayMode !== 'items'}
        mode={displayMode === 'items' ? 'points' : displayMode}
        selectedCount={selectedMemberIds.size}
        onClose={() => setBulkModalOpen(false)}
        onApplyToBlocks={applyBulkToBlocks}
        onGrant={grantFromBulkModal}
        pointTypeLabel={bulkPointTypeLabel}
        onOpenPointTypeSelect={() => {
          setPointTypeSelectSource('bulk');
          setSelectingMemberId(null);
          setPointTypeSelectOpen(true);
        }}
        pointValue={bulkPointValue}
        onPointValueChange={setBulkPointValue}
        pointMemo={bulkPointMemo}
        onPointMemoChange={setBulkPointMemo}
        coinAmount={bulkCoinAmount}
        onCoinAmountChange={setBulkCoinAmount}
        coinStep={10}
        coinMemo={bulkCoinMemo}
        onCoinMemoChange={setBulkCoinMemo}
        noteText={bulkNoteText}
        onNoteTextChange={setBulkNoteText}
        noteType={bulkNoteType}
        onNoteTypeCycle={() =>
          setBulkNoteType((prev) =>
            prev === '일반' ? '학습' : prev === '학습' ? '행동' : '일반'
          )
        }
      />
      <PointTypeSelectModal
        open={pointTypeSelectOpen}
        pointSet={doc.pointSet}
        onClose={() => {
          setPointTypeSelectOpen(false);
          setSelectingMemberId(null);
        }}
        onSelect={applyPointTypeSelectResult}
        onEditSets={() => setPointSetOpen(true)}
        onRefresh={() => {
          void load();
        }}
      />
      <PointTypeSetModal
        open={pointSetOpen}
        pointSet={doc.pointSet}
        onClose={() => setPointSetOpen(false)}
        onSave={updatePointSet}
      />
      <ItemCardSettingsModal
        open={itemCardOpen}
        itemCards={doc.itemCards}
        itemCardImages={doc.itemCardImages ?? []}
        workspaceId={workspaceId}
        onClose={() => setItemCardOpen(false)}
        onSave={updateItemCards}
        onItemCardImagesChange={updateItemCardImages}
      />
      <ClassPointSettingsModal
        open={classPointOpen}
        settings={doc.classPointSettings ?? emptyClassPointSettings()}
        itemCardImages={doc.itemCardImages ?? []}
        classTotalPoints={classIntegratedPointTotal}
        levelUpPoints={doc.levelSettings.levelUpPoints || doc.pointSet.levelUpPoints || 50}
        memberCount={members.length}
        onClose={() => setClassPointOpen(false)}
        onSave={updateClassPointSettings}
        onItemCardImagesChange={updateItemCardImages}
      />
      <ClassPointClaimReveal
        open={classPointClaimReveal != null}
        cards={classPointClaimReveal?.cards ?? []}
        mode={classPointClaimReveal?.mode ?? 'grant'}
        onClose={() => setClassPointClaimReveal(null)}
      />
      <MemberItemGrantModal
        open={itemGrantOpen}
        itemCards={doc.itemCards}
        targets={itemGrantTargetIds.map((id) => {
          const m = allMembers.find((x) => x.id === id);
          return {
            id,
            label: m
              ? `${String(m.orderIndex).padStart(2, '0')}. ${displayClassMemberName(m).trim() || '(이름 없음)'}`
              : id,
          };
        })}
        onClose={() => setItemGrantOpen(false)}
        onAddToCart={addBulkStorePurchaseDraft}
      />
      <ItemGrantSummaryModal
        open={itemSummaryOpen}
        itemName={itemSummaryName}
        acquired={itemSummaryAcquired}
        missed={itemSummaryMissed}
        onClose={() => setItemSummaryOpen(false)}
      />
      <MemberItemInventoryModal
        open={itemInventoryMemberId != null}
        member={itemInventoryMember}
        instances={groupPoints.itemInstances ?? []}
        itemCards={doc.itemCards}
        extraItemCards={classPointExtraCards}
        getLevelColor={getLevelColor}
        onClose={() => setItemInventoryMemberId(null)}
        onUseItem={useInventoryItem}
        onDeleteItem={deleteInventoryItem}
        profileCardUnlocks={groupPoints.profileCardUnlocks ?? []}
        profileCardSets={profileCardSets}
        onExchangeProfileCardDuplicates={exchangeProfileCardDuplicates}
        onDeleteProfileCardUnlock={deleteProfileCardUnlock}
      />
      <MemberStoreModal
        open={storeMemberId != null || storeClassOpen}
        storeItems={storeProfileOnly ? [] : doc.itemCards.storeItem ?? []}
        currentCoins={
          storeMemberId
            ? Math.max(
                0,
                memberDisplayCoinTotal(groupPoints, storeMemberId) -
                  (storeProfileOnly ? 0 : pendingStoreCostForMember(storeMemberId))
              )
            : 0
        }
        memberLabel={
          storeMemberId
            ? (() => {
                const m = allMembers.find((x) => x.id === storeMemberId);
                return m
                  ? `${String(m.orderIndex).padStart(2, '0')} ${displayClassMemberName(m).trim() || '(이름 없음)'}`
                  : undefined;
              })()
            : undefined
        }
        buyers={
          storeClassOpen
            ? members.map(
                (m): StoreBuyerOption => ({
                  id: m.id,
                  name: displayClassMemberName(m),
                  orderIndex: m.orderIndex,
                  coins: Math.max(
                    0,
                    memberDisplayCoinTotal(groupPoints, m.id) - pendingStoreCostForMember(m.id)
                  ),
                  member: m,
                })
              )
            : undefined
        }
        workspaceId={workspaceId}
        gradeId={currentGradeId}
        onClose={() => {
          setStoreMemberId(null);
          setStoreClassOpen(false);
          setStoreProfileOnly(false);
        }}
        onPurchase={(card) => {
          if (!storeMemberId || storeProfileOnly) return;
          addStorePurchaseDraft(storeMemberId, card);
        }}
        onAddToCart={(card) => {
          // 개별 멤버 스토어에서의 장바구니 담기
          if (storeMemberId && !storeProfileOnly) {
            // 아이템 탭 조회 모드의 hover 기록 블럭에서 연 스토어면 모드 유지(해당 학생만 담김)
            if (!(displayMode === 'items' && !isRecordMode)) {
              // 아이템 탭으로 전환하고 전체 기록 모드 활성화
              setDisplayModeAndPersist('items');
              setInteractionModeAndPersist('record');
            }
            addStorePurchaseDraft(storeMemberId, card);
          }
        }}
        onAddToCartMany={(card, memberIds) => {
          // 클래스 스토어에서의 장바구니 담기
          // 아이템 탭으로 전환하고 전체 기록 모드 활성화
          setDisplayModeAndPersist('items');
          setInteractionModeAndPersist('record');
          // 선택된 모든 멤버에게 장바구니 담기
          memberIds.forEach((id) => addStorePurchaseDraft(id, card));
        }}
        onPurchaseMany={(card, memberIds) => {
          memberIds.forEach((id) => addStorePurchaseDraft(id, card));
        }}
        profileSets={storeProfileCardSets}
        profileOnly={storeProfileOnly}
        initialTab={storeProfileOnly ? 'profile' : 'item'}
        onPurchaseProfileCard={(set) => {
          if (!storeMemberId) return { ok: false, reason: 'ALL_UNLOCKED' };
          const result = purchaseProfileCardNow(storeMemberId, set);
          if (result.ok && storeProfileOnly) {
            setProfileUnlocksRefreshKey((k) => k + 1);
          }
          return result;
        }}
        onPurchaseProfileCardMany={(set, memberIds) => {
          let okCount = 0;
          let lastOk: Extract<ProfileCardPurchaseResult, { ok: true }> | null = null;
          for (const id of memberIds) {
            const result = purchaseProfileCardNow(id, set);
            if (result.ok) {
              okCount += 1;
              lastOk = result;
            }
          }
          return { okCount, lastOk };
        }}
      />
      {/* 프로필 이미지 설정 — 헤더 버튼(첫 학생 선택)·블럭 프로필 더블클릭(해당 학생 자동 선택) 공용 */}
      <ProfileImageSelectModal
        open={bulkProfileOpen || profileSelectMemberId != null}
        initialMemberId={profileSelectMemberId ?? undefined}
        members={members}
        workspaceId={workspaceId}
        gradeId={currentGradeId}
        unlocksRefreshKey={profileUnlocksRefreshKey}
        onMembersPhotoChange={(next) => {
          const byId = new Map(next.map((r) => [r.id, r]));
          setAllMembers((prev) =>
            prev.map((m) => {
              const n = byId.get(m.id);
              if (!n) return m;
              return {
                ...m,
                photoProfilePath: n.photoProfilePath,
                photoProfileUrl: n.photoProfileUrl,
                photoProfileVersion: n.photoProfileVersion,
              };
            })
          );
        }}
        onClose={() => {
          setBulkProfileOpen(false);
          setProfileSelectMemberId(null);
        }}
        onConfirm={(changes) => {
          Object.entries(changes).forEach(([id, img]) => applyProfileImageLocal(id, img));
        }}
        onOpenStore={openProfileStoreFromSelectModal}
      />
      <LevelSettingsModal
        open={levelSettingsOpen}
        levelSettings={doc.levelSettings}
        levelupItems={doc.itemCards.levelupItem}
        pointStep={1}
        coinStep={10}
        onClose={() => setLevelSettingsOpen(false)}
        onSave={updateLevelSettings}
      />
      <TimeTagSelectModal
        open={timeTagPickerOpen}
        onClose={() => setTimeTagPickerOpen(false)}
        tags={scheduleTags}
        selected={selectedTimeTag}
        dayLabel={dayBadge}
        dayKey={dayKeyFromDate(selectedDate)}
        customPrefill="now"
        onSelect={(tag) => {
          timeTagManualRef.current = true;
          setSelectedTimeTag(tag);
        }}
      />
      <PointItemDateCalendarModal
        open={dateCalendarOpen}
        selectedDateIso={selectedDateIso}
        onClose={() => setDateCalendarOpen(false)}
        onSelect={(iso) => setSelectedDateIso(iso)}
      />
      <MemberRecordHistoryModal
        open={historyMemberId != null}
        onClose={() => setHistoryMemberId(null)}
        memberName={historyMember ? displayClassMemberName(historyMember).trim() || '(이름 없음)' : '(이름 없음)'}
        title={historyTitle}
        mode={displayMode === 'items' ? 'points' : displayMode}
        rows={historyRows}
        assignmentRows={assignmentHistoryRows}
        getTimeTagsForDate={getHistoryTimeTagsForDate}
        onDeleteRecords={deleteHistoryRecords}
        onUpdateRecords={updateHistoryRecords}
      />
      {gainFloats.length > 0
        ? createPortal(
            <div className="cp-pin-gain-float-layer" aria-hidden>
              {gainFloats.map((f) => (
                <span
                  key={f.key}
                  className={`cp-pin-gain-float is-${f.kind}${f.value < 0 ? ' is-minus' : ''}`}
                  style={{ left: f.x, top: f.y }}
                >
                  {f.value > 0 ? `+${f.value}` : `${f.value}`}
                </span>
              ))}
            </div>,
            document.body
          )
        : null}
      <button
        type="button"
        className="cp-fab-save"
        disabled={saving || !isDirty || !groupDataReady}
        onClick={() => void handleSave()}
        aria-label={
          saving
            ? displayMode === 'items'
              ? '구매 중'
              : '저장 중'
            : displayMode === 'items'
              ? '구매하기'
              : '저장하기'
        }
        title={
          displayMode === 'items'
            ? isDirty
              ? '구매하기'
              : '구매할 변경사항 없음'
            : isDirty
              ? '저장하기'
              : '저장할 변경사항 없음'
        }
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
          alt=""
          className="cp-fab-save-icon"
        />
      </button>
      {modePillFloating && surfaceActive
        ? createPortal(
            <div
              className={
                'cp-pin-mode-float-bar' + (isPresentation ? ' is-presentation' : '')
              }
              role="presentation"
            >
              <button
                type="button"
                className={`cp-pin-mode-pill cp-pin-mode-pill--float${
                  isRecordMode ? ' is-record' : ' is-view'
                }`}
                aria-pressed={isRecordMode}
                aria-label={
                  isRecordMode
                    ? '전체 기록 모드, 클릭하면 개별 기록 모드로 전환'
                    : '개별 기록 모드, 클릭하면 전체 기록 모드로 전환'
                }
                onClick={toggleInteractionMode}
              >
                <span className="cp-pin-mode-label">
                  {isRecordMode ? '전체 기록 모드' : '개별 기록 모드'}
                </span>
                <span className="cp-pin-mode-knob" aria-hidden>
                  {isRecordMode ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M20.71 7.04c.39-.39.39-1.02 0-1.41L18.37 3.29a1 1 0 00-1.41 0l-.88.88 3.75 3.75.88-.88z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2" />
                      <path
                        d="M20 20L16.65 16.65"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </span>
              </button>
            </div>,
            document.body
          )
        : null}
      {saveToastState ? <AutoStatusToast state={saveToastState} /> : null}
    </div>
  );
};

export default PointItemNotePage;
